/**
 * 虚拟精灵养成游戏 - 像素风格
 * Virtual Pet Care Game - Pixel Art Style
 */

import './styles.css';
import * as PetShapes from './pet-shapes';

console.log('Main script loaded');

// 语言类型
type Language = 'en' | 'zh';

// 翻译文本
const translations = {
  en: {
    petTypes: {
      fire: 'Fire', water: 'Water', earth: 'Earth', air: 'Air',
      steam: 'Steam', lava: 'Lava', lightning: 'Lightning', swamp: 'Swamp',
      frost: 'Frost', dust: 'Dust', blaze: 'Blaze', ocean: 'Ocean',
      volcano: 'Volcano', aurora: 'Aurora', tide: 'Tide', plasma: 'Plasma',
      snowstorm: 'Snowstorm', typhoon: 'Typhoon', inferno: 'Inferno', mudstone: 'Mudstone',
      geyser: 'Geyser', thunderstorm: 'Thunderstorm', glacier: 'Glacier', wildfire: 'Wildfire',
      reef: 'Reef', sandstorm: 'Sandstorm', haze: 'Haze', magma: 'Magma',
      fairy: 'Fairy'
    },
    ui: {
      coins: 'Coins',
      pokedex: 'Pokedex',
      reset: 'Reset',
      switchPet: 'Switch',
      myPets: 'My Pets',
      hunger: 'Hunger',
      happiness: 'Happiness',
      cleanliness: 'Clean',
      energy: 'Energy',
      feed: 'Feed',
      play: 'Play',
      clean: 'Clean',
      sleep: 'Sleep',
      breed: 'Breed',
      maxLevel: 'MAX LEVEL',
      exp: 'EXP',
      resetConfirm: 'Reset Confirmation',
      resetWarning: 'Warning: This action cannot be undone',
      resetDescription: 'Reset will:\n• Delete all pet data\n• Clear all progress and coins\n• Return to initial state',
      confirmReset: 'Are you sure you want to reset the game?',
      cancel: 'Cancel',
      confirmButton: 'Confirm Reset',
      resetSuccess: '🎮 Restart! New pet born!',
      switchPetTitle: 'Switch Pet',
      closeButton: '✕',
      language: 'Language',
      working: 'Working',
      resting: 'Resting',
      protected: 'Protected',
      release: 'Release',
      breedSuccess: '💕 Breed Success 💕',
      congratulations: 'Congratulations on breeding a new pet!',
      newPetJoined: 'New pet has joined your team\nGo interact with it!',
      awesome: 'Awesome!',
      breedRequirement: '⚠️ Breeding requires: Happiness ≥ 80, Energy ≥ 50',
      selectBreedPets: 'Select Pets to Breed',
      breedButton: '💕 Breed',
      cannotBreed: 'Cannot Breed',
      selected: 'Selected',
      releaseConfirm: '⚠️ Release Confirmation',
      petName: 'Name',
      petType: 'Type',
      releaseWarning: '⚠️ Cannot recover after release',
      confirmRelease: 'Confirm Release',
      petManagement: 'Pet Management',
      switch: 'Switch',
      selectLanguage: 'Select Language',
      workRewards: 'Work Rewards',
      totalReward: 'Total Reward',
      collectAll: 'Collect All',
      unlockSlot: 'Unlock Slot',
      price: 'Price',
      myPetsList: 'My Pets',
      stopWork: 'Stop Work',
      startWork: 'Start Work',
      rest: 'Rest',
      collect: 'Collect',
      series: 'Series',
      needMorePets: 'Need {count} more pets to breed',
      totalEarnings: 'Total Earnings',
      expand: 'Expand',
      pet1: 'Pet 1',
      pet2: 'Pet 2',
      pleaseSelect: '-- Please Select --',
      notSelected: 'Not Selected',
      unlockProgress: 'Unlock Progress',
      locked: 'Locked',
      // Notifications
      noti: {
        elementalNoFeed: 'Elemental spirits don\'t need food!',
        alreadyFull: 'Already full!',
        tooDirty: 'Too dirty! Take a bath first...',
        ateWell: 'Delicious! +80 EXP',
        elementalEternalHappy: 'Elemental spirits are eternally happy!',
        tooTired: 'Too tired, need rest...',
        tooHungry: 'Too hungry! Eat something first...',
        playedWell: 'Had so much fun! +120 EXP',
        elementalEternalClean: 'Elemental spirits are eternally clean!',
        alreadyClean: 'Already very clean!',
        cleanedWell: 'Feels so refreshed! +100 EXP',
        elementalInfiniteEnergy: 'Elemental spirits have infinite energy!',
        notSleepy: 'Not sleepy yet!',
        tooDirtyToSleep: 'Too dirty! Take a bath before sleeping...',
        tooHungryToSleep: 'Too hungry! Eat something before sleeping...',
        sleptWell: 'Slept so well! Energy restored! +60 EXP',
        noSlot: 'No free slot! Unlock a new slot first',
        lowHappiness: 'Happiness too low! Make the pet happier before breeding',
        lowEnergy: 'Energy insufficient! Let the pet rest first',
        newPetBorn: '🎉 New pet born!',
        protected: '🛡️ This pet is protected and cannot be released!',
        released: '🕊️ {name} has been released, wish it freedom and happiness!',
        collectSuccess: '💰 Collected successfully! Gained {amount} coins',
        startWorking: '{name} started working!',
        finishWorking: '💰 {name} finished working! Gained {amount} coins',
        stopWorking: '{name} stopped working',
        maxSlots: 'Maximum number of slots reached!',
        notEnoughCoins: 'Not enough coins! Need {amount} coins',
        slotUnlocked: '🎉 New slot unlocked! Now you have {count} slots',
        fairyHatched: '✨ A miracle happened! Hatched a rare Fairy pet egg!',
        breedSuccessEgg: '💕 Breed success! Got a {type} Series pet egg!'
      }
    }
  },
  zh: {
    petTypes: {
      fire: '火', water: '水', earth: '土', air: '气',
      steam: '蒸汽', lava: '熔岩', lightning: '闪电', swamp: '沼泽',
      frost: '冰霜', dust: '尘埃', blaze: '烈焰', ocean: '海洋',
      volcano: '火山', aurora: '极光', tide: '潮汐', plasma: '等离子',
      snowstorm: '暴雪', typhoon: '台风', inferno: '炼狱', mudstone: '泥石',
      geyser: '间歇泉', thunderstorm: '雷暴', glacier: '冰川', wildfire: '野火',
      reef: '珊瑚', sandstorm: '沙尘暴', haze: '雾霾', magma: '岩浆',
      fairy: '混沌'
    },
    ui: {
      coins: '金币',
      pokedex: '图鉴',
      reset: '重置',
      switchPet: '更换',
      myPets: '我的精灵',
      hunger: '饥饿度',
      happiness: '快乐度',
      cleanliness: '清洁度',
      energy: '能量值',
      feed: '喂食',
      play: '玩耍',
      clean: '清洁',
      sleep: '睡眠',
      breed: '合成',
      maxLevel: '最高等级',
      exp: '经验',
      resetConfirm: '重置确认',
      resetWarning: '警告：此操作不可恢复',
      resetDescription: '重置将会：\n• 删除所有精灵数据\n• 清空所有进度和金币\n• 返回游戏初始状态',
      confirmReset: '确定要重置游戏吗？',
      cancel: '取消',
      confirmButton: '确定重置',
      resetSuccess: '🎮 重新开始！新的精灵诞生了！',
      switchPetTitle: '更换精灵',
      closeButton: '✕',
      language: '语言',
      working: '打工中',
      resting: '休息中',
      protected: '受保护',
      release: '放生',
      breedSuccess: '💕 合成成功 💕',
      congratulations: '恭喜你合成出了新精灵！',
      newPetJoined: '新精灵已经加入你的队伍\n快去和它互动吧！',
      awesome: '太棒了！',
      breedRequirement: '⚠️ 合成要求：快乐度 ≥ 80、能量 ≥ 50',
      selectBreedPets: '选择合成精灵',
      breedButton: '💕 精灵合成',
      cannotBreed: '无法合成',
      selected: '已选',
      releaseConfirm: '⚠️ 放生确认',
      petName: '名称',
      petType: '属性',
      releaseWarning: '⚠️ 放生后将无法找回',
      confirmRelease: '确定放生',
      petManagement: '精灵管理',
      switch: '更换',
      selectLanguage: '选择语言',
      workRewards: '打工收益',
      totalReward: '总收益',
      collectAll: '收取全部',
      unlockSlot: '解锁槽位',
      price: '价格',
      myPetsList: '我的精灵',
      stopWork: '停止打工',
      startWork: '开始打工',
      rest: '休息',
      collect: '收取',
      series: '系',
      needMorePets: '还需要 {count} 只精灵才能合成',
      totalEarnings: '总收益',
      expand: '展开查看',
      pet1: '精灵 1',
      pet2: '精灵 2',
      pleaseSelect: '-- 请选择 --',
      notSelected: '未选择',
      unlockProgress: '解锁进度',
      locked: '未解锁',
      // Notifications
      noti: {
        elementalNoFeed: '创世元素精灵不需要进食！',
        alreadyFull: '已经很饱了！',
        tooDirty: '太脏了！先洗个澡吧...',
        ateWell: '好好吃！+80 EXP',
        elementalEternalHappy: '创世元素精灵永恒快乐！',
        tooTired: '太累了,需要休息...',
        tooHungry: '太饿了！先吃点东西吧...',
        playedWell: '玩得好开心！+120 EXP',
        elementalEternalClean: '创世元素精灵永恒洁净！',
        alreadyClean: '已经很干净了！',
        cleanedWell: '洗得真舒服！+100 EXP',
        elementalInfiniteEnergy: '创世元素精灵能量无限！',
        notSleepy: '还不困呢！',
        tooDirtyToSleep: '太脏了！先洗个澡再睡觉...',
        tooHungryToSleep: '太饿了！吃点东西再睡吧...',
        sleptWell: '睡得好香！能量恢复！+60 EXP',
        noSlot: '没有空闲槽位了！先解锁新槽位吧',
        lowHappiness: '快乐度太低了！让精灵开心点再合成吧',
        lowEnergy: '能量不足！先让精灵休息一下',
        newPetBorn: '🎉 新的精灵诞生了！',
        protected: '🛡️ 该精灵受保护,无法放生!',
        released: '🕊️ {name} 已被放生，祝它自由快乐！',
        collectSuccess: '💰 收取成功！获得{amount}金币',
        startWorking: '{name} 开始打工了！',
        finishWorking: '💰 {name} 完成打工！获得{amount}金币',
        stopWorking: '{name} 停止打工',
        maxSlots: '已达到最大槽位数量！',
        notEnoughCoins: '金币不足！需要{amount}金币',
        slotUnlocked: '🎉 解锁新槽位！现在有{count}个槽位',
        fairyHatched: '✨ 奇迹发生了！孵化出了稀有的混沌系精灵蛋！',
        breedSuccessEgg: '💕 合成成功！获得了{type}系精灵蛋！'
      }
    }
  }
};

// 精灵类型定义
type PetType = 'fire' | 'water' | 'earth' | 'air' |
  'steam' | 'lava' | 'lightning' | 'swamp' | 'frost' | 'dust' | 'blaze' | 'ocean' |
  'volcano' | 'aurora' | 'tide' | 'plasma' | 'snowstorm' | 'typhoon' | 'inferno' | 'mudstone' |
  'geyser' | 'thunderstorm' | 'glacier' | 'wildfire' | 'reef' | 'sandstorm' | 'haze' | 'magma' |
  'fairy';

// 精灵进化阶段
type EvolutionStage = 'egg' | 'baby' | 'teen' | 'adult' | 'master';

// 精灵状态接口
interface PetState {
  id: string;
  name: string;
  type: PetType;
  hunger: number;
  happiness: number;
  cleanliness: number;
  energy: number;
  age: number;
  birthTime: number;
  lastUpdateTime: number;
  isWorking?: boolean; // 是否正在打工
  workStartTime?: number; // 打工开始时间
  isProtected?: boolean; // 是否受保护(不可放生)
}

// 游戏全局状态
interface GameState {
  pets: PetState[];
  currentPetIndex: number;
  unlockedSlots: number; // 已解锁的精灵槽位数量
  coins: number; // 金币数量
  unlockedPetTypes?: PetType[]; // 已解锁的精灵类型
  language?: Language; // 当前语言
}

// 像素风格精灵配置
const PET_CONFIG = {
  // 创世四元素
  fire: { color: '#ff4444', secondColor: '#ff8800' },
  water: { color: '#4488ff', secondColor: '#00ccff' },
  earth: { color: '#8b6914', secondColor: '#d4a017' },
  air: { color: '#e0f7fa', secondColor: '#b2ebf2' },

  // 一代合成：配种产出的8种新属性
  steam: { color: '#b0bec5', secondColor: '#90a4ae' },
  lava: { color: '#ff6b35', secondColor: '#ff9f1c' },
  lightning: { color: '#ffeb3b', secondColor: '#fdd835' },
  swamp: { color: '#558b2f', secondColor: '#689f38' },
  frost: { color: '#81d4fa', secondColor: '#4fc3f7' },
  dust: { color: '#bcaaa4', secondColor: '#a1887f' },
  blaze: { color: '#ff1744', secondColor: '#ff5252' },
  ocean: { color: '#0277bd', secondColor: '#0288d1' },

  // 二代合成：一代精灵配种产出的16种新属性
  volcano: { color: '#d84315', secondColor: '#ff6f00' },
  aurora: { color: '#7c4dff', secondColor: '#b388ff' },
  tide: { color: '#00695c', secondColor: '#00897b' },
  plasma: { color: '#ff6d00', secondColor: '#ff9e40' },
  snowstorm: { color: '#e1f5fe', secondColor: '#b3e5fc' },
  typhoon: { color: '#263238', secondColor: '#455a64' },
  inferno: { color: '#b71c1c', secondColor: '#d32f2f' },
  mudstone: { color: '#5d4037', secondColor: '#6d4c41' },
  geyser: { color: '#78909c', secondColor: '#90a4ae' },
  thunderstorm: { color: '#1a237e', secondColor: '#283593' },
  glacier: { color: '#4dd0e1', secondColor: '#80deea' },
  wildfire: { color: '#ff3d00', secondColor: '#ff6e40' },
  reef: { color: '#00acc1', secondColor: '#26c6da' },
  sandstorm: { color: '#f57c00', secondColor: '#fb8c00' },
  haze: { color: '#757575', secondColor: '#9e9e9e' },
  magma: { color: '#bf360c', secondColor: '#d84315' },

  // 稀有混沌系
  fairy: { color: '#ff77ff', secondColor: '#ffccff' },
};

const STAGE_CONFIG = {
  egg: { name: '蛋', expRequired: 0 },      // Lv1 初始
  baby: { name: '幼年期', expRequired: 100 },   // Lv2 (100经验)
  teen: { name: '成长期', expRequired: 600 },   // Lv4-5 (累计600经验)
  adult: { name: '成熟期', expRequired: 2100 },  // Lv8-9 (累计2100经验)
  master: { name: '完全体', expRequired: 5500 }, // Lv12-13 (累计5500经验)
};

const MAX_LEVEL = 15;
const MAX_PETS = 100; // 精灵栏最高上限100
const FAIRY_HATCH_RATE = 0.05; // 5% 概率孵化仙系
const SLOT_UNLOCK_PRICE = 100; // 每个槽位解锁价格

// 受保护的精灵类型(不可放生) - 仅初始四元素
const PROTECTED_PET_TYPES: PetType[] = [
  // 创世四元素
  'fire', 'water', 'earth', 'air'
];

// 配种组合规则
const BREED_COMBINATIONS: Record<string, PetType> = {
  // 一代配种：基础元素 → 一代合成
  'fire-water': 'steam',
  'water-fire': 'steam',
  'fire-earth': 'lava',
  'earth-fire': 'lava',
  'fire-air': 'lightning',
  'air-fire': 'lightning',
  'water-earth': 'swamp',
  'earth-water': 'swamp',
  'water-air': 'frost',
  'air-water': 'frost',
  'earth-air': 'dust',
  'air-earth': 'dust',
  'fire-fire': 'blaze',
  'water-water': 'ocean',
  'earth-earth': 'earth',
  'air-air': 'air',

  // 二代配种：一代合成 → 二代合成
  'steam-lava': 'volcano',      // 蒸汽 + 熔岩 = 火山
  'lava-steam': 'volcano',
  'lightning-frost': 'aurora',   // 闪电 + 冰霜 = 极光
  'frost-lightning': 'aurora',
  'swamp-ocean': 'tide',        // 沼泽 + 海洋 = 潮汐
  'ocean-swamp': 'tide',
  'blaze-lightning': 'plasma',   // 烈焰 + 闪电 = 等离子
  'lightning-blaze': 'plasma',
  'frost-dust': 'snowstorm',    // 冰霜 + 尘埃 = 雪暴
  'dust-frost': 'snowstorm',
  'steam-ocean': 'typhoon',     // 蒸汽 + 海洋 = 台风
  'ocean-steam': 'typhoon',
  'lava-blaze': 'inferno',      // 熔岩 + 烈焰 = 炼狱
  'blaze-lava': 'inferno',
  'swamp-dust': 'mudstone',     // 沼泽 + 尘埃 = 泥石
  'dust-swamp': 'mudstone',
  'steam-frost': 'geyser',      // 蒸汽 + 冰霜 = 间歇泉
  'frost-steam': 'geyser',
  'lightning-ocean': 'thunderstorm', // 闪电 + 海洋 = 雷暴
  'ocean-lightning': 'thunderstorm',
  'frost-ocean': 'glacier',     // 冰霜 + 海洋 = 冰川
  'ocean-frost': 'glacier',
  'blaze-dust': 'wildfire',     // 烈焰 + 尘埃 = 野火
  'dust-blaze': 'wildfire',
  'dust-lightning': 'sandstorm', // 尘埃 + 闪电 = 沙暴
  'lightning-dust': 'sandstorm',
  'steam-dust': 'haze',         // 蒸汽 + 尘埃 = 雾霾
  'dust-steam': 'haze',
  'lava-swamp': 'magma',        // 熔岩 + 沼泽 = 岩浆
  'swamp-lava': 'magma',
  'swamp-frost': 'reef',        // 沼泽 + 冰霜 = 珊瑚礁
  'frost-swamp': 'reef',

  // 一代同属性配种保持原属性
  'steam-steam': 'steam',
  'lava-lava': 'lava',
  'lightning-lightning': 'lightning',
  'swamp-swamp': 'swamp',
  'frost-frost': 'frost',
  'dust-dust': 'dust',
  'blaze-blaze': 'blaze',
  'ocean-ocean': 'ocean',
};

class GameManager {
  private gameState: GameState;
  private updateInterval: number | null = null;
  private container: HTMLElement;
  private currentLanguage: Language = 'en'; // 默认英文

  constructor(container: HTMLElement) {
    console.log('GameManager constructor called');
    this.container = container;
    this.gameState = this.loadGameState() || this.createNewGame();
    this.currentLanguage = this.gameState.language || 'en';

    // 迁移旧精灵名称为翻译后的名称
    this.migratePetNames();

    console.log('Initial game state:', this.gameState);
    this.startAutoUpdate();
  }

  // 获取翻译文本
  private t(key: string): string {
    const keys = key.split('.');
    let value: any = translations[this.currentLanguage];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  }

  // 切换语言
  private switchLanguage(lang: Language): void {
    this.currentLanguage = lang;
    this.gameState.language = lang;

    // 切换语言时更新所有精灵名称
    this.migratePetNames();

    this.saveGameState();
    this.render();
  }

  // 迁移旧精灵名称为翻译后的名称
  private migratePetNames(): void {
    console.log('Migrating pet names to current language:', this.currentLanguage);
    let updated = false;
    this.gameState.pets.forEach(pet => {
      const translatedName = this.t(`petTypes.${pet.type}`);
      if (pet.name !== translatedName) {
        console.log(`Updating pet name from "${pet.name}" to "${translatedName}"`);
        pet.name = translatedName;
        updated = true;
      }
    });

    if (updated) {
      console.log('Pet names updated, saving game state');
      this.saveGameState();
    } else {
      console.log('No pet name migration needed');
    }
  }

  private createNewGame(): GameState {
    console.log('Creating new game');

    // 创建8只初始精灵（创世四元素各2只）
    const elementalTypes: PetType[] = ['fire', 'water', 'earth', 'air'];

    // 每种元素创建2只
    const pets: PetState[] = [];
    elementalTypes.forEach(type => {
      pets.push(this.createNewPet(type, true));
      pets.push(this.createNewPet(type, true));
    });

    // 打乱顺序
    pets.sort(() => Math.random() - 0.5);

    return {
      pets: pets,
      currentPetIndex: 0,
      unlockedSlots: 20, // 初始有20个槽位
      coins: 0,
      unlockedPetTypes: ['fire', 'water', 'earth', 'air'], // 初始解锁四元素
      language: 'en', // 默认英文
    };
  }

  private createNewPet(parentType?: PetType, isInitial: boolean = false): PetState {
    let randomType: PetType;

    // 如果是初始精灵且提供了类型，直接使用
    if (isInitial && parentType) {
      randomType = parentType;
    } else if (parentType) {
      // 配种时根据父母属性产生新属性
      // 同属性配种会产生对应的新属性或保持原属性
      const breedKey = `${parentType}-${parentType}`;
      randomType = BREED_COMBINATIONS[breedKey] || parentType;
    } else {
      // 随机生成创世四元素之一
      const elementalTypes: PetType[] = ['fire', 'water', 'earth', 'air'];
      const randomIndex = Math.floor(Math.random() * elementalTypes.length);
      randomType = elementalTypes[randomIndex];
    }

    // 所有精灵直接从成熟期开始，可以立即合成
    return {
      id: Date.now().toString() + Math.random(),
      name: this.t(`petTypes.${randomType}`), // 使用翻译后的属性名称作为精灵名字
      type: randomType,
      hunger: 100,
      happiness: 100,
      cleanliness: 100,
      energy: 100,
      age: 0,
      birthTime: Date.now(),
      lastUpdateTime: Date.now(),
      isWorking: true, // 自动开始打工
      workStartTime: Date.now(), // 记录打工开始时间
      isProtected: isInitial && PROTECTED_PET_TYPES.includes(randomType), // 只有初始的四元素精灵受保护
    };
  }

  private getCurrentPet(): PetState {
    return this.gameState.pets[this.gameState.currentPetIndex];
  }

  private loadGameState(): GameState | null {
    try {
      const saved = localStorage.getItem('virtualPetGame');
      if (saved) {
        const state = JSON.parse(saved) as GameState;

        // 验证数据
        if (!state.pets || state.pets.length === 0) {
          return null;
        }

        // 应用离线时间并兼容旧版本数据
        state.pets.forEach(pet => {
          this.applyOfflineTime(pet);
          // 为旧存档启动打工
          if (pet.isWorking === undefined) {
            pet.isWorking = true;
            pet.workStartTime = Date.now();
          }
        });

        // 为旧存档添加coins属性
        if (state.coins === undefined) {
          state.coins = 0;
        }

        // 确保至少有4个槽位
        if (state.unlockedSlots === undefined || state.unlockedSlots < 4) {
          state.unlockedSlots = 4;
        }

        // 为旧存档补充到4只创世元素精灵
        if (state.pets.length < 4) {
          const elementalTypes: PetType[] = ['fire', 'water', 'earth', 'air'];
          const existingTypes = state.pets.map(p => p.type);

          // 找出缺失的元素类型
          const missingTypes = elementalTypes.filter(type => !existingTypes.includes(type));

          // 为每个缺失的类型创建精灵
          missingTypes.forEach(type => {
            const newPet = this.createNewPet(type, true);
            state.pets.push(newPet);
          });
        }

        // 为旧存档初始化图鉴解锁状态
        if (!state.unlockedPetTypes) {
          // 根据现有精灵类型初始化已解锁列表
          state.unlockedPetTypes = [...new Set(state.pets.map(pet => pet.type))];
        }

        // 数据迁移：修正放生保护状态，只有初始四元素才受保护
        state.pets.forEach(pet => {
          const isElemental = ['fire', 'water', 'earth', 'air'].includes(pet.type);
          // 如果不是四元素，则移除保护状态
          if (!isElemental) {
            pet.isProtected = false;
          }
          // 如果是四元素，确保有保护状态
          if (isElemental && pet.isProtected === undefined) {
            pet.isProtected = true;
          }
        });

        console.log('Loaded game state from localStorage:', state);
        return state;
      }
    } catch (error) {
      console.error('Error loading game state:', error);
      localStorage.removeItem('virtualPetGame');
    }
    return null;
  }

  private saveGameState(): void {
    try {
      this.gameState.pets.forEach(pet => {
        pet.lastUpdateTime = Date.now();
      });
      localStorage.setItem('virtualPetGame', JSON.stringify(this.gameState));
    } catch (error) {
      console.error('Error saving game state:', error);
    }
  }

  private applyOfflineTime(state: PetState): void {
    const now = Date.now();
    const timePassed = now - state.lastUpdateTime;
    const minutesPassed = Math.floor(timePassed / 60000);

    if (minutesPassed > 0) {
      // 四元素精灵数值永远满值,不衰减
      const isElemental = ['fire', 'water', 'earth', 'air'].includes(state.type);

      if (isElemental) {
        // 四元素精灵保持满值
        state.hunger = 100;
        state.happiness = 100;
        state.cleanliness = 100;
        state.energy = 100;
      } else {
        // 其他精灵正常衰减
        state.hunger = Math.max(0, state.hunger - minutesPassed * 0.5);
        state.happiness = Math.max(0, state.happiness - 0.3);
        state.cleanliness = Math.max(0, state.cleanliness - minutesPassed * 0.2);
        state.energy = Math.min(100, state.energy + minutesPassed * 0.5);
      }
      state.age += minutesPassed;
    }
  }

  private startAutoUpdate(): void {
    this.updateInterval = window.setInterval(() => {
      this.updatePetStatus();
      this.checkEvolution();
      this.saveGameState();
      this.updateDisplay();

      // 30% 概率触发随机待机动画
      if (Math.random() < 0.3) {
        this.triggerRandomIdleAnimation();
      }
    }, 5000);
  }

  private updateDisplay(): void {
    const pet = this.getCurrentPet();

    // 只更新动态数据,不重新渲染整个DOM
    const hungerBar = document.querySelector('.stat-item:nth-child(1) .stat-fill') as HTMLElement;
    const hungerValue = document.querySelector('.stat-item:nth-child(1) .stat-value') as HTMLElement;
    const happinessBar = document.querySelector('.stat-item:nth-child(2) .stat-fill') as HTMLElement;
    const happinessValue = document.querySelector('.stat-item:nth-child(2) .stat-value') as HTMLElement;
    const cleanlinessBar = document.querySelector('.stat-item:nth-child(3) .stat-fill') as HTMLElement;
    const cleanlinessValue = document.querySelector('.stat-item:nth-child(3) .stat-value') as HTMLElement;
    const energyBar = document.querySelector('.stat-item:nth-child(4) .stat-fill') as HTMLElement;
    const energyValue = document.querySelector('.stat-item:nth-child(4) .stat-value') as HTMLElement;

    if (hungerBar) {
      hungerBar.style.width = `${pet.hunger}%`;
      hungerBar.style.background = this.getStatusColor(pet.hunger);
    }
    if (hungerValue) hungerValue.textContent = `${Math.round(pet.hunger)}%`;

    if (happinessBar) {
      happinessBar.style.width = `${pet.happiness}%`;
      happinessBar.style.background = this.getStatusColor(pet.happiness);
    }
    if (happinessValue) happinessValue.textContent = `${Math.round(pet.happiness)}%`;

    if (cleanlinessBar) {
      cleanlinessBar.style.width = `${pet.cleanliness}%`;
      cleanlinessBar.style.background = this.getStatusColor(pet.cleanliness);
    }
    if (cleanlinessValue) cleanlinessValue.textContent = `${Math.round(pet.cleanliness)}%`;

    if (energyBar) {
      energyBar.style.width = `${pet.energy}%`;
      energyBar.style.background = this.getStatusColor(pet.energy);
    }
    if (energyValue) energyValue.textContent = `${Math.round(pet.energy)}%`;

    // 更新表情图标
    const hungerIcon = document.querySelector('.stat-item:nth-child(1) .stat-icon') as HTMLElement;
    const happinessIcon = document.querySelector('.stat-item:nth-child(2) .stat-icon') as HTMLElement;
    const cleanlinessIcon = document.querySelector('.stat-item:nth-child(3) .stat-icon') as HTMLElement;
    const energyIcon = document.querySelector('.stat-item:nth-child(4) .stat-icon') as HTMLElement;

    if (hungerIcon) hungerIcon.textContent = this.getStatusEmoji(pet.hunger);
    if (happinessIcon) happinessIcon.textContent = this.getStatusEmoji(pet.happiness);
    if (cleanlinessIcon) cleanlinessIcon.textContent = this.getStatusEmoji(pet.cleanliness);
    if (energyIcon) energyIcon.textContent = this.getStatusEmoji(pet.energy);
  }

  private updatePetStatus(): void {
    const pet = this.getCurrentPet();

    // 四元素精灵数值永远满值,不衰减
    const isElemental = ['fire', 'water', 'earth', 'air'].includes(pet.type);

    if (isElemental) {
      // 四元素精灵保持满值
      pet.hunger = 100;
      pet.happiness = 100;
      pet.cleanliness = 100;
      pet.energy = 100;
    } else {
      // 其他精灵正常衰减
      pet.hunger = Math.max(0, pet.hunger - 0.1);
      pet.happiness = Math.max(0, pet.happiness - 0.05);
      pet.cleanliness = Math.max(0, pet.cleanliness - 0.08);
      pet.energy = Math.min(100, pet.energy + 0.1);
    }
    pet.age += 0.083;
  }

  private checkEvolution(): void {
    // 精灵直接从成熟期开始，不需要进化逻辑
  }

  private evolve(newStage: EvolutionStage): void {
    // 精灵直接从成熟期开始，不需要进化逻辑
  }

  private updatePetDisplay(): void {
    const pet = this.getCurrentPet();
    const petConfig = PET_CONFIG[pet.type];

    // 更新精灵像素图形
    const petContainer = document.querySelector('.pet-container');
    if (petContainer) {
      petContainer.innerHTML = this.generatePixelPet();
    }

    // 更新精灵显示区域的颜色变量
    const petDisplay = document.querySelector('.pet-display') as HTMLElement;
    if (petDisplay) {
      petDisplay.style.setProperty('--pet-color', petConfig.color);
      petDisplay.style.setProperty('--pet-color-2', petConfig.secondColor);
    }
  }

  private gainExp(amount: number): void {
    // 取消等级系统，不再需要经验获取逻辑
  }

  private checkSlotUnlock(): void {
    // 取消等级系统，不再有满级解锁槽位的逻辑
  }

  feed(): void {
    const pet = this.getCurrentPet();

    // 四元素精灵不需要喂食
    const isElemental = ['fire', 'water', 'earth', 'air'].includes(pet.type);
    if (isElemental) {
      this.showNotification(this.t('ui.noti.elementalNoFeed'));
      this.triggerPetAnimation('shake');
      return;
    }

    if (pet.hunger >= 95) {
      this.showNotification(this.t('ui.noti.alreadyFull'));
      this.triggerPetAnimation('shake');
      return;
    }

    // 太脏会影响食欲
    if (pet.cleanliness < 30) {
      this.showNotification(this.t('ui.noti.tooDirty'));
      this.triggerPetAnimation('shake');
      return;
    }

    pet.hunger = Math.min(100, pet.hunger + 20);
    pet.happiness = Math.min(100, pet.happiness + 5);
    // 吃饱了会恢复一些能量
    pet.energy = Math.min(100, pet.energy + 5);
    this.gainExp(80);
    this.showNotification(this.t('ui.noti.ateWell'));
    this.triggerPetAnimation('eat');
    this.saveGameState();
    this.updateDisplay();
  }

  play(): void {
    const pet = this.getCurrentPet();

    // 四元素精灵不需要玩耍
    const isElemental = ['fire', 'water', 'earth', 'air'].includes(pet.type);
    if (isElemental) {
      this.showNotification(this.t('ui.noti.elementalEternalHappy'));
      this.triggerPetAnimation('jump');
      return;
    }

    if (pet.energy < 20) {
      this.showNotification(this.t('ui.noti.tooTired'));
      this.triggerPetAnimation('tired');
      return;
    }

    // 太饿了会影响玩耍
    if (pet.hunger < 30) {
      this.showNotification(this.t('ui.noti.tooHungry'));
      this.triggerPetAnimation('shake');
      return;
    }

    pet.happiness = Math.min(100, pet.happiness + 15);
    pet.energy = Math.max(0, pet.energy - 20);
    pet.hunger = Math.max(0, pet.hunger - 10);
    // 玩耍会弄脏
    pet.cleanliness = Math.max(0, pet.cleanliness - 8);
    this.gainExp(120);
    this.showNotification(this.t('ui.noti.playedWell'));
    this.triggerPetAnimation('jump');
    this.saveGameState();
    this.updateDisplay();
  }

  clean(): void {
    const pet = this.getCurrentPet();

    // 四元素精灵不需要清洁
    const isElemental = ['fire', 'water', 'earth', 'air'].includes(pet.type);
    if (isElemental) {
      this.showNotification(this.t('ui.noti.elementalEternalClean'));
      this.triggerPetAnimation('spin');
      return;
    }

    if (pet.cleanliness >= 95) {
      this.showNotification(this.t('ui.noti.alreadyClean'));
      this.triggerPetAnimation('shake');
      return;
    }

    pet.cleanliness = 100;
    pet.happiness = Math.min(100, pet.happiness + 8);
    // 洗澡会消耗一些能量
    pet.energy = Math.max(0, pet.energy - 5);
    this.gainExp(100);
    this.showNotification(this.t('ui.noti.cleanedWell'));
    this.triggerPetAnimation('spin');
    this.saveGameState();
    this.updateDisplay();
  }

  sleep(): void {
    const pet = this.getCurrentPet();

    // 四元素精灵不需要睡眠
    const isElemental = ['fire', 'water', 'earth', 'air'].includes(pet.type);
    if (isElemental) {
      this.showNotification(this.t('ui.noti.elementalInfiniteEnergy'));
      this.triggerPetAnimation('sleep');
      return;
    }

    if (pet.energy >= 95) {
      this.showNotification(this.t('ui.noti.notSleepy'));
      this.triggerPetAnimation('shake');
      return;
    }

    // 太脏需要先洗澡
    if (pet.cleanliness < 50) {
      this.showNotification(this.t('ui.noti.tooDirtyToSleep'));
      this.triggerPetAnimation('shake');
      return;
    }

    // 太饿会影响睡眠
    if (pet.hunger < 40) {
      this.showNotification(this.t('ui.noti.tooHungryToSleep'));
      this.triggerPetAnimation('shake');
      return;
    }

    pet.energy = 100;
    pet.hunger = Math.max(0, pet.hunger - 15);
    // 睡眠提升快乐度
    pet.happiness = Math.min(100, pet.happiness + 10);
    this.gainExp(60);
    this.showNotification(this.t('ui.noti.sleptWell'));
    this.triggerPetAnimation('sleep');
    this.saveGameState();
    this.updateDisplay();
  }

  breed(): void {
    const pet = this.getCurrentPet();

    // 检查是否有空闲槽位
    if (this.gameState.pets.length >= this.gameState.unlockedSlots) {
      this.showNotification(this.t('ui.noti.noSlot'));
      this.triggerPetAnimation('shake');
      return;
    }

    // 检查精灵状态
    if (pet.happiness < 80) {
      this.showNotification(this.t('ui.noti.lowHappiness'));
      this.triggerPetAnimation('shake');
      return;
    }

    if (pet.energy < 50) {
      this.showNotification(this.t('ui.noti.lowEnergy'));
      this.triggerPetAnimation('tired');
      return;
    }

    // 合成成功,创建新精灵蛋
    const newPet = this.createNewPet(pet.type);
    this.gameState.pets.push(newPet);

    // 合成消耗状态（四元素精灵不消耗）
    const isElemental = ['fire', 'water', 'earth', 'air'].includes(pet.type);
    if (!isElemental) {
      pet.energy = Math.max(0, pet.energy - 30);
      pet.happiness = Math.max(0, pet.happiness - 20);
      pet.hunger = Math.max(0, pet.hunger - 15);
    }

    const isFairy = newPet.type === 'fairy';
    const message = isFairy ?
      this.t('ui.noti.fairyHatched') :
      this.t('ui.noti.breedSuccessEgg').replace('{type}', this.t(`petTypes.${newPet.type}`));

    this.showNotification(message);
    this.triggerPetAnimation('jump');
    this.saveGameState();
    this.updateDisplay();
  }

  // 根据精灵ID进行合成（用于精灵管理界面）
  private breedPetById(petId: string): void {
    const pet = this.gameState.pets.find(p => p.id === petId);
    if (!pet) return;

    // 检查是否有空闲槽位
    if (this.gameState.pets.length >= this.gameState.unlockedSlots) {
      this.showNotification(this.t('ui.noti.noSlot'));
      return;
    }

    // 检查精灵状态
    if (pet.happiness < 80) {
      this.showNotification(this.t('ui.noti.lowHappiness'));
      return;
    }

    if (pet.energy < 50) {
      this.showNotification(this.t('ui.noti.lowEnergy'));
      return;
    }

    // 合成成功,创建新精灵蛋
    const newPet = this.createNewPet(pet.type);
    this.gameState.pets.push(newPet);

    // 解锁新类型到图鉴
    if (!this.gameState.unlockedPetTypes) {
      this.gameState.unlockedPetTypes = [];
    }
    if (!this.gameState.unlockedPetTypes.includes(newPet.type)) {
      this.gameState.unlockedPetTypes.push(newPet.type);
    }

    // 合成消耗状态（四元素精灵不消耗）
    const isElemental = ['fire', 'water', 'earth', 'air'].includes(pet.type);
    if (!isElemental) {
      pet.energy = Math.max(0, pet.energy - 30);
      pet.happiness = Math.max(0, pet.happiness - 20);
      pet.hunger = Math.max(0, pet.hunger - 15);
    }

    const isFairy = newPet.type === 'fairy';
    const message = isFairy ?
      this.t('ui.noti.fairyHatched') :
      this.t('ui.noti.breedSuccessEgg').replace('{type}', this.t(`petTypes.${newPet.type}`));

    this.showNotification(message);
    this.saveGameState();
    this.updateDisplay();
  }

  private triggerPetAnimation(type: 'eat' | 'jump' | 'spin' | 'shake' | 'sleep' | 'tired'): void {
    const petElement = document.querySelector('.pixel-pet') as HTMLElement;
    if (!petElement) return;

    // 移除所有动画类
    petElement.classList.remove('anim-eat', 'anim-jump', 'anim-spin', 'anim-shake', 'anim-sleep', 'anim-tired');

    // 添加对应动画类
    void petElement.offsetWidth; // 触发重排
    petElement.classList.add(`anim-${type}`);

    // 动画结束后移除类
    setTimeout(() => {
      petElement.classList.remove(`anim-${type}`);
    }, 1000);
  }

  private triggerRandomIdleAnimation(): void {
    const petElement = document.querySelector('.pixel-pet') as HTMLElement;
    if (!petElement) return;

    // 根据精灵状态选择合适的待机动画
    const animations = this.getIdleAnimationsForState();
    const randomAnim = animations[Math.floor(Math.random() * animations.length)];

    // 移除所有待机动画类
    petElement.classList.remove('idle-bounce', 'idle-wiggle', 'idle-blink', 'idle-nod', 'idle-stretch', 'idle-yawn', 'idle-dance', 'idle-float', 'idle-sway', 'idle-breathe');

    // 添加随机待机动画
    void petElement.offsetWidth;
    petElement.classList.add(randomAnim);

    // 2秒后移除动画类
    setTimeout(() => {
      petElement.classList.remove(randomAnim);
    }, 2000);
  }

  private getIdleAnimationsForState(): string[] {
    const pet = this.getCurrentPet();
    const baseAnimations = ['idle-bounce', 'idle-blink', 'idle-breathe'];

    // 根据快乐度添加不同动画
    if (pet.happiness >= 70) {
      baseAnimations.push('idle-dance', 'idle-wiggle', 'idle-nod');
    }

    // 根据能量值添加不同动画
    if (pet.energy < 30) {
      baseAnimations.push('idle-yawn', 'idle-sway');
    } else if (pet.energy >= 70) {
      baseAnimations.push('idle-float', 'idle-stretch');
    }

    // 饥饿时的特殊动画
    if (pet.hunger < 40) {
      baseAnimations.push('idle-sway', 'idle-yawn');
    }

    return baseAnimations;
  }

  changeName(newName: string): void {
    const pet = this.getCurrentPet();
    if (newName.trim() && newName.trim() !== pet.name) {
      pet.name = newName.trim();
      this.saveGameState();
      // 不重新渲染,避免失去焦点
    }
  }

  // 显示重置确认弹窗
  private showResetConfirmation(): void {
    const confirmHtml = `
      <div class="modal-overlay" id="resetConfirmModal" style="z-index: 10000;">
        <div class="modal-content pixel-border" style="max-width: 500px;">
          <div class="modal-header pixel-border-bottom">
            <h2 class="pixel-text" style="color: #ff4757;">${this.t('ui.resetConfirm')}</h2>
            <button class="modal-close pixel-btn" id="closeResetConfirmBtn">✕</button>
          </div>
          <div class="modal-body" style="padding: 20px;">
            <div style="padding: 20px; background: rgba(255, 71, 87, 0.1); border: 2px solid #ff4757; border-radius: 8px; margin-bottom: 20px;">
              <div style="font-size: 16px; margin-bottom: 12px; text-align: center; color: #ff4757;">
                ${this.t('ui.resetWarning')}
              </div>
              <div style="font-size: 14px; color: #ccc; line-height: 1.8;">
                ${this.t('ui.resetDescription').replace(/\n/g, '<br>')}
              </div>
            </div>
            <div style="font-size: 15px; text-align: center; color: #ffaa00; margin-bottom: 20px;">
              ${this.t('ui.confirmReset')}
            </div>
            <div style="display: flex; gap: 12px;">
              <button class="pixel-btn" id="cancelResetBtn" style="flex: 1; background: linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%); border: 2px solid #7f8c8d;">
                ${this.t('ui.cancel')}
              </button>
              <button class="pixel-btn" id="confirmResetBtn" style="flex: 1; background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%); border: 2px solid #e74c3c;">
                ${this.t('ui.confirmButton')}
              </button>
            </div>
          </div>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', confirmHtml);

    const modal = document.getElementById('resetConfirmModal');
    const closeBtn = document.getElementById('closeResetConfirmBtn');
    const cancelBtn = document.getElementById('cancelResetBtn');
    const confirmBtn = document.getElementById('confirmResetBtn');

    const closeModal = () => {
      modal?.classList.remove('show');
      setTimeout(() => modal?.remove(), 300);
    };

    closeBtn?.addEventListener('click', closeModal);
    cancelBtn?.addEventListener('click', closeModal);
    modal?.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });

    confirmBtn?.addEventListener('click', () => {
      closeModal();
      this.reset();
    });

    // 显示动画
    setTimeout(() => {
      modal?.classList.add('show');
    }, 10);
  }

  reset(): void {
    localStorage.removeItem('virtualPetGame');
    this.gameState = this.createNewGame();
    this.saveGameState();
    this.render();
    this.showNotification(this.t('ui.resetSuccess'));
  }

  private showNotification(message: string): void {
    const petDisplay = document.querySelector('.pet-display');
    if (!petDisplay) return;

    // 获取精灵显示区域的位置
    const rect = petDisplay.getBoundingClientRect();

    const notification = document.createElement('div');
    notification.className = 'notification pixel-text';
    notification.textContent = message;

    // 设置提示框位置（在精灵区域外部上方）
    notification.style.left = `${rect.left + rect.width / 2}px`;
    notification.style.top = `${rect.top}px`;

    document.body.appendChild(notification);

    setTimeout(() => {
      notification.classList.add('show');
    }, 10);

    setTimeout(() => {
      notification.classList.remove('show');
      setTimeout(() => notification.remove(), 400);
    }, 2000);
  }

  private getStatusColor(value: number): string {
    if (value >= 70) return '#4caf50';
    if (value >= 40) return '#ff9800';
    return '#f44336';
  }

  private getStatusEmoji(value: number): string {
    if (value >= 70) return '◕‿◕';
    if (value >= 40) return '◕_◕';
    return '◕︵◕';
  }

  private generatePixelPet(): string {
    const pet = this.getCurrentPet();
    const { type } = pet;
    const petConfig = PET_CONFIG[type];
    const stage = 'adult'; // 所有精灵统一显示成年形态

    // 根据类型返回不同的精灵形象
    const petShapes: Record<PetType, string> = {
      fire: PetShapes.generateFirePet(stage, petConfig),
      water: PetShapes.generateWaterPet(stage, petConfig),
      earth: PetShapes.generateEarthPet(stage, petConfig),
      air: PetShapes.generateAirPet(stage, petConfig),
      steam: PetShapes.generateSteamPet(stage, petConfig),
      lava: PetShapes.generateLavaPet(stage, petConfig),
      lightning: PetShapes.generateLightningPet(stage, petConfig),
      swamp: PetShapes.generateSwampPet(stage, petConfig),
      frost: PetShapes.generateFrostPet(stage, petConfig),
      dust: PetShapes.generateDustPet(stage, petConfig),
      blaze: PetShapes.generateBlazePet(stage, petConfig),
      ocean: PetShapes.generateOceanPet(stage, petConfig),
      fairy: PetShapes.generateFairyPet(stage, petConfig),
      // 二代精灵使用专门的生成函数
      volcano: PetShapes.generateVolcanoPet(stage, petConfig),
      aurora: PetShapes.generateAuroraPet(stage, petConfig),
      tide: PetShapes.generateTidePet(stage, petConfig),
      plasma: PetShapes.generatePlasmaPet(stage, petConfig),
      snowstorm: PetShapes.generateSnowstormPet(stage, petConfig),
      typhoon: PetShapes.generateTyphoonPet(stage, petConfig),
      inferno: PetShapes.generateInfernoPet(stage, petConfig),
      mudstone: PetShapes.generateMudstonePet(stage, petConfig),
      geyser: PetShapes.generateGeyserPet(stage, petConfig),
      thunderstorm: PetShapes.generateThunderstormPet(stage, petConfig),
      glacier: PetShapes.generateGlacierPet(stage, petConfig),
      wildfire: PetShapes.generateWildfirePet(stage, petConfig),
      reef: PetShapes.generateReefPet(stage, petConfig),
      sandstorm: PetShapes.generateSandstormPet(stage, petConfig),
      haze: PetShapes.generateHazePet(stage, petConfig),
      magma: PetShapes.generateMagmaPet(stage, petConfig),
    };

    return petShapes[type] || PetShapes.generateFirePet(stage, petConfig);
  }

  // 通用二代精灵生成器（为新精灵快速生成像素艺术）
  private generateGenericPet(stage: EvolutionStage, config: any, type: PetType): string {
    if (stage === 'egg') {
      return `
        <div class="pixel-pet pixel-egg" data-type="${type}" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span></span><span></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span></span><span></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span></span><span></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span></span><span></span></div>
        </div>
      `;
    }

    if (stage === 'baby') {
      return `
        <div class="pixel-pet pixel-baby" data-type="${type}" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span></span><span></span><span class="p1"></span><span></span><span class="p1"></span><span></span><span></span></div>
        </div>
      `;
    }

    if (stage === 'teen') {
      return `
        <div class="pixel-pet pixel-teen" data-type="${type}" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p3"></span><span class="p1"></span><span class="p1"></span><span class="p3"></span><span class="p3"></span><span></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span></span><span class="p1"></span><span class="p1"></span><span></span><span class="p1"></span><span></span></div>
        </div>
      `;
    }

    if (stage === 'adult') {
      return `
        <div class="pixel-pet pixel-adult" data-type="${type}" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span class="p3"></span><span class="p3"></span><span></span><span class="p1"></span><span class="p1"></span><span></span><span class="p3"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span></span><span></span><span></span><span></span><span class="p1"></span><span></span></div>
        </div>
      `;
    }

    return `
      <div class="pixel-pet pixel-master" data-type="${type}" style="--pet-color: ${config.color};">
        <div class="pixel-row"><span class="p3"></span><span class="p3"></span><span class="p3"></span><span></span><span></span><span class="p3"></span><span class="p3"></span><span class="p3"></span></div>
        <div class="pixel-row"><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p1"></span><span class="p1"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span></div>
        <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
        <div class="pixel-row"><span></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span></span></div>
        <div class="pixel-row"><span></span><span class="p1"></span><span></span><span></span><span></span><span></span><span class="p1"></span><span></span></div>
      </div>
    `;
  }

  // 火系精灵（火焰形状）
  private generateFirePet(stage: EvolutionStage, config: any): string {
    if (stage === 'egg') {
      return `
        <div class="pixel-pet pixel-egg" data-type="fire" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span></span><span></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span></span><span></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span></span><span></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span></span><span></span></div>
        </div>
      `;
    }

    if (stage === 'baby') {
      return `
        <div class="pixel-pet pixel-baby" data-type="fire" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span></span><span></span><span class="p1"></span><span></span><span class="p1"></span><span></span><span></span></div>
        </div>
      `;
    }

    if (stage === 'teen') {
      return `
        <div class="pixel-pet pixel-teen" data-type="fire" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p3"></span><span class="p1"></span><span class="p1"></span><span class="p3"></span><span class="p3"></span><span></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span></span><span class="p1"></span><span class="p1"></span><span></span><span class="p1"></span><span></span></div>
        </div>
      `;
    }

    if (stage === 'adult') {
      return `
        <div class="pixel-pet pixel-adult" data-type="fire" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span class="p3"></span><span class="p3"></span><span></span><span class="p1"></span><span class="p1"></span><span></span><span class="p3"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span></span><span></span><span></span><span></span><span class="p1"></span><span></span></div>
        </div>
      `;
    }

    return `
      <div class="pixel-pet pixel-master" data-type="fire" style="--pet-color: ${config.color};">
        <div class="pixel-row"><span class="p3"></span><span class="p3"></span><span class="p3"></span><span></span><span></span><span class="p3"></span><span class="p3"></span><span class="p3"></span></div>
        <div class="pixel-row"><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p1"></span><span class="p1"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span></div>
        <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
        <div class="pixel-row"><span></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span></span></div>
        <div class="pixel-row"><span></span><span class="p1"></span><span></span><span></span><span></span><span></span><span class="p1"></span><span></span></div>
      </div>
    `;
  }

  // 水系精灵（波浪/水滴怪兽形状）
  private generateWaterPet(stage: EvolutionStage, config: any): string {
    if (stage === 'egg') {
      return `
        <div class="pixel-pet pixel-egg" data-type="water" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span></span><span></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span></span><span></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span></span><span></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span></span><span></span></div>
        </div>
      `;
    }

    if (stage === 'baby') {
      return `
        <div class="pixel-pet pixel-baby" data-type="water" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p3"></span><span></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span class="p3"></span><span></span><span class="p1"></span><span></span><span class="p1"></span><span></span><span class="p3"></span></div>
        </div>
      `;
    }

    if (stage === 'teen') {
      return `
        <div class="pixel-pet pixel-teen" data-type="water" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span class="p3"></span><span class="p3"></span><span></span><span class="p1"></span><span class="p1"></span><span></span><span class="p3"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p3"></span><span></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span></span><span class="p3"></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span class="p3"></span><span></span><span class="p1"></span><span></span><span></span><span class="p1"></span><span></span><span class="p3"></span></div>
        </div>
      `;
    }

    if (stage === 'adult') {
      return `
        <div class="pixel-pet pixel-adult" data-type="water" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span class="p3"></span><span class="p3"></span><span></span><span class="p1"></span><span class="p1"></span><span></span><span class="p3"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p3"></span><span></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span></span><span class="p3"></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span class="p3"></span><span></span><span class="p1"></span><span></span><span></span><span class="p1"></span><span></span><span class="p3"></span></div>
        </div>
      `;
    }

    return `
      <div class="pixel-pet pixel-master" data-type="water" style="--pet-color: ${config.color};">
        <div class="pixel-row"><span class="p3"></span><span class="p3"></span><span class="p3"></span><span></span><span></span><span class="p3"></span><span class="p3"></span><span class="p3"></span></div>
        <div class="pixel-row"><span class="p3"></span><span class="p3"></span><span></span><span class="p1"></span><span class="p1"></span><span></span><span class="p3"></span><span class="p3"></span></div>
        <div class="pixel-row"><span class="p3"></span><span></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span></span><span class="p3"></span></div>
        <div class="pixel-row"><span></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
        <div class="pixel-row"><span></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span></span></div>
        <div class="pixel-row"><span class="p3"></span><span></span><span class="p1"></span><span></span><span></span><span class="p1"></span><span></span><span class="p3"></span></div>
      </div>
    `;
  }

  // 土系精灵（方块/岩石怪兽形状）
  private generateEarthPet(stage: EvolutionStage, config: any): string {
    if (stage === 'egg') {
      return `
        <div class="pixel-pet pixel-egg" data-type="earth" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span></span></div>
        </div>
      `;
    }

    if (stage === 'baby') {
      return `
        <div class="pixel-pet pixel-baby" data-type="earth" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span></span><span class="p1"></span><span></span><span class="p1"></span><span></span></div>
        </div>
      `;
    }

    if (stage === 'teen') {
      return `
        <div class="pixel-pet pixel-teen" data-type="earth" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span></div>
        </div>
      `;
    }

    if (stage === 'adult') {
      return `
        <div class="pixel-pet pixel-adult" data-type="earth" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p1"></span><span></span><span></span><span></span><span></span><span class="p1"></span><span class="p1"></span></div>
        </div>
      `;
    }

    return `
      <div class="pixel-pet pixel-master" data-type="earth" style="--pet-color: ${config.color};">
        <div class="pixel-row"><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p1"></span><span></span><span></span><span></span><span></span><span class="p1"></span><span class="p1"></span></div>
      </div>
    `;
  }

  // 气系精灵（云朵/风精灵形状）
  private generateAirPet(stage: EvolutionStage, config: any): string {
    if (stage === 'egg') {
      return `
        <div class="pixel-pet pixel-egg" data-type="air" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span></span><span></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span></span><span></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span></span><span></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span></span><span></span></div>
        </div>
      `;
    }

    if (stage === 'baby') {
      return `
        <div class="pixel-pet pixel-baby" data-type="air" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span class="p3"></span><span></span><span class="p1"></span><span></span><span class="p1"></span><span></span><span class="p3"></span></div>
        </div>
      `;
    }

    if (stage === 'teen') {
      return `
        <div class="pixel-pet pixel-teen" data-type="air" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span class="p3"></span><span></span><span class="p1"></span><span></span><span></span><span class="p1"></span><span></span><span class="p3"></span></div>
        </div>
      `;
    }

    if (stage === 'adult') {
      return `
        <div class="pixel-pet pixel-adult" data-type="air" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span class="p3"></span><span></span><span class="p1"></span><span></span><span></span><span class="p1"></span><span></span><span class="p3"></span></div>
        </div>
      `;
    }

    return `
      <div class="pixel-pet pixel-master" data-type="air" style="--pet-color: ${config.color};">
        <div class="pixel-row"><span class="p3"></span><span class="p3"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p3"></span><span class="p3"></span></div>
        <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p3"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
        <div class="pixel-row"><span></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span></span></div>
        <div class="pixel-row"><span class="p3"></span><span></span><span class="p1"></span><span></span><span></span><span class="p1"></span><span></span><span class="p3"></span></div>
      </div>
    `;
  }

  // 蒸汽系精灵（云雾怪兽）
  private generateSteamPet(stage: EvolutionStage, config: any): string {
    if (stage === 'egg') {
      return `
        <div class="pixel-pet pixel-egg" data-type="steam" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span></span></div>
        </div>
      `;
    }

    if (stage === 'baby') {
      return `
        <div class="pixel-pet pixel-baby" data-type="steam" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p3"></span><span></span><span class="p3"></span><span class="p3"></span><span></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span></span></div>
          <div class="pixel-row"><span class="p3"></span><span></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span></span><span class="p3"></span></div>
        </div>
      `;
    }

    if (stage === 'teen') {
      return `
        <div class="pixel-pet pixel-teen" data-type="steam" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span class="p3"></span><span></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span></span><span class="p3"></span></div>
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span></span></div>
          <div class="pixel-row"><span class="p3"></span><span></span><span class="p3"></span><span></span><span class="p3"></span><span></span><span class="p3"></span></div>
        </div>
      `;
    }

    if (stage === 'adult') {
      return `
        <div class="pixel-pet pixel-adult" data-type="steam" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span></span><span class="p3"></span><span></span><span class="p3"></span><span class="p3"></span><span></span><span class="p3"></span><span></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p1"></span><span></span><span></span><span class="p1"></span><span class="p3"></span><span></span></div>
          <div class="pixel-row"><span class="p3"></span><span></span><span class="p3"></span><span></span><span></span><span class="p3"></span><span></span><span class="p3"></span></div>
        </div>
      `;
    }

    // master
    return `
      <div class="pixel-pet pixel-master" data-type="steam" style="--pet-color: ${config.color};">
        <div class="pixel-row"><span class="p3"></span><span></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span></span><span class="p3"></span></div>
        <div class="pixel-row"><span></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span></span></div>
        <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
        <div class="pixel-row"><span></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span></span></div>
        <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
        <div class="pixel-row"><span></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span></span></div>
        <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
        <div class="pixel-row"><span></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span></span></div>
        <div class="pixel-row"><span class="p3"></span><span></span><span class="p3"></span><span></span><span></span><span class="p3"></span><span></span><span class="p3"></span></div>
      </div>
    `;
  }

  // 熔岩系精灵（岩浆怪兽）
  private generateLavaPet(stage: EvolutionStage, config: any): string {
    if (stage === 'egg') {
      return `
        <div class="pixel-pet pixel-egg" data-type="lava" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span></div>
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span></span></div>
        </div>
      `;
    }

    if (stage === 'baby') {
      return `
        <div class="pixel-pet pixel-baby" data-type="lava" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span></div>
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span></span></div>
        </div>
      `;
    }

    if (stage === 'teen') {
      return `
        <div class="pixel-pet pixel-teen" data-type="lava" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p3"></span><span></span><span class="p3"></span><span class="p3"></span><span></span></div>
        </div>
      `;
    }

    if (stage === 'adult') {
      return `
        <div class="pixel-pet pixel-adult" data-type="lava" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span></div>
          <div class="pixel-row"><span></span><span class="p3"></span><span></span><span class="p3"></span><span class="p3"></span><span></span><span class="p3"></span><span></span></div>
        </div>
      `;
    }

    // master
    return `
      <div class="pixel-pet pixel-master" data-type="lava" style="--pet-color: ${config.color};">
        <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span></div>
        <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span></div>
        <div class="pixel-row"><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span></div>
        <div class="pixel-row"><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span></div>
        <div class="pixel-row"><span></span><span></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span></span><span></span></div>
      </div>
    `;
  }

  // 闪电系精灵（电光怪兽）
  private generateLightningPet(stage: EvolutionStage, config: any): string {
    if (stage === 'egg') {
      return `
        <div class="pixel-pet pixel-egg" data-type="lightning" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span></span><span></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span></span><span></span></div>
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span></span></div>
          <div class="pixel-row"><span></span><span></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span></span><span></span></div>
        </div>
      `;
    }

    if (stage === 'baby') {
      return `
        <div class="pixel-pet pixel-baby" data-type="lightning" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p1"></span><span></span><span class="p1"></span><span class="p3"></span><span></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span></div>
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span></span></div>
        </div>
      `;
    }

    if (stage === 'teen') {
      return `
        <div class="pixel-pet pixel-teen" data-type="lightning" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span></span><span class="p1"></span><span></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span></span></div>
          <div class="pixel-row"><span class="p3"></span><span></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span></span><span class="p3"></span></div>
        </div>
      `;
    }

    if (stage === 'adult') {
      return `
        <div class="pixel-pet pixel-adult" data-type="lightning" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span></span><span class="p1"></span><span class="p1"></span><span></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span></span></div>
          <div class="pixel-row"><span class="p3"></span><span></span><span class="p3"></span><span></span><span></span><span class="p3"></span><span></span><span class="p3"></span></div>
        </div>
      `;
    }

    // master
    return `
      <div class="pixel-pet pixel-master" data-type="lightning" style="--pet-color: ${config.color};">
        <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span></span><span class="p1"></span><span class="p1"></span><span></span><span class="p1"></span><span class="p3"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span></div>
        <div class="pixel-row"><span></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span></div>
        <div class="pixel-row"><span></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span></span></div>
        <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span></div>
        <div class="pixel-row"><span></span><span class="p3"></span><span></span><span></span><span></span><span></span><span class="p3"></span><span></span></div>
      </div>
    `;
  }

  // 沼泽系精灵（沼泽怪兽）
  private generateSwampPet(stage: EvolutionStage, config: any): string {
    if (stage === 'egg') {
      return `
        <div class="pixel-pet pixel-egg" data-type="swamp" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span></div>
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span></span></div>
        </div>
      `;
    }

    if (stage === 'baby') {
      return `
        <div class="pixel-pet pixel-baby" data-type="swamp" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span></div>
        </div>
      `;
    }

    if (stage === 'teen') {
      return `
        <div class="pixel-pet pixel-teen" data-type="swamp" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span></div>
        </div>
      `;
    }

    if (stage === 'adult') {
      return `
        <div class="pixel-pet pixel-adult" data-type="swamp" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span></div>
        </div>
      `;
    }

    // master
    return `
      <div class="pixel-pet pixel-master" data-type="swamp" style="--pet-color: ${config.color};">
        <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span></div>
        <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span></div>
        <div class="pixel-row"><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span></div>
        <div class="pixel-row"><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span></div>
      </div>
    `;
  }

  // 冰霜系精灵（冰晶怪兽）
  private generateFrostPet(stage: EvolutionStage, config: any): string {
    if (stage === 'egg') {
      return `
        <div class="pixel-pet pixel-egg" data-type="frost" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span></span></div>
        </div>
      `;
    }

    if (stage === 'baby') {
      return `
        <div class="pixel-pet pixel-baby" data-type="frost" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span class="p1"></span><span></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span></span></div>
        </div>
      `;
    }

    if (stage === 'teen') {
      return `
        <div class="pixel-pet pixel-teen" data-type="frost" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span class="p1"></span><span></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p1"></span><span></span></div>
        </div>
      `;
    }

    if (stage === 'adult') {
      return `
        <div class="pixel-pet pixel-adult" data-type="frost" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span class="p1"></span><span></span><span class="p3"></span><span class="p1"></span><span class="p1"></span><span class="p3"></span><span></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span></span></div>
          <div class="pixel-row"><span class="p1"></span><span></span><span class="p3"></span><span></span><span></span><span class="p3"></span><span></span><span class="p1"></span></div>
        </div>
      `;
    }

    // master
    return `
      <div class="pixel-pet pixel-master" data-type="frost" style="--pet-color: ${config.color};">
        <div class="pixel-row"><span class="p1"></span><span></span><span class="p3"></span><span class="p1"></span><span class="p1"></span><span class="p3"></span><span></span><span class="p1"></span></div>
        <div class="pixel-row"><span></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span></span></div>
        <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
        <div class="pixel-row"><span></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span></span></div>
        <div class="pixel-row"><span class="p1"></span><span></span><span class="p3"></span><span></span><span></span><span class="p3"></span><span></span><span class="p1"></span></div>
      </div>
    `;
  }

  // 尘埃系精灵（沙尘怪兽）
  private generateDustPet(stage: EvolutionStage, config: any): string {
    if (stage === 'egg') {
      return `
        <div class="pixel-pet pixel-egg" data-type="dust" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span></div>
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span></span></div>
        </div>
      `;
    }

    if (stage === 'baby') {
      return `
        <div class="pixel-pet pixel-baby" data-type="dust" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span></div>
        </div>
      `;
    }

    if (stage === 'teen') {
      return `
        <div class="pixel-pet pixel-teen" data-type="dust" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span></div>
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span></span></div>
        </div>
      `;
    }

    if (stage === 'adult') {
      return `
        <div class="pixel-pet pixel-adult" data-type="dust" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span></div>
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span></span></div>
        </div>
      `;
    }

    // master
    return `
      <div class="pixel-pet pixel-master" data-type="dust" style="--pet-color: ${config.color};">
        <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span></div>
        <div class="pixel-row"><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span></div>
        <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span></div>
        <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span></div>
        <div class="pixel-row"><span></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span></span></div>
      </div>
    `;
  }

  // 烈焰系精灵（强化火焰怪兽）
  private generateBlazePet(stage: EvolutionStage, config: any): string {
    if (stage === 'egg') {
      return `
        <div class="pixel-pet pixel-egg" data-type="blaze" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span></span></div>
        </div>
      `;
    }

    if (stage === 'baby') {
      return `
        <div class="pixel-pet pixel-baby" data-type="blaze" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span class="p1"></span><span></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span></div>
        </div>
      `;
    }

    if (stage === 'teen') {
      return `
        <div class="pixel-pet pixel-teen" data-type="blaze" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span class="p1"></span><span></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span></div>
        </div>
      `;
    }

    if (stage === 'adult') {
      return `
        <div class="pixel-pet pixel-adult" data-type="blaze" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span class="p1"></span><span></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span></div>
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span></span></div>
        </div>
      `;
    }

    // master
    return `
      <div class="pixel-pet pixel-master" data-type="blaze" style="--pet-color: ${config.color};">
        <div class="pixel-row"><span class="p1"></span><span></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span></span><span class="p1"></span></div>
        <div class="pixel-row"><span></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span></span></div>
        <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span></div>
        <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span></div>
        <div class="pixel-row"><span></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span class="p3"></span><span></span></div>
      </div>
    `;
  }

  // 海洋系精灵（深海怪兽）
  private generateOceanPet(stage: EvolutionStage, config: any): string {
    if (stage === 'egg') {
      return `
        <div class="pixel-pet pixel-egg" data-type="ocean" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span></div>
          <div class="pixel-row"><span></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span></span></div>
        </div>
      `;
    }

    if (stage === 'baby') {
      return `
        <div class="pixel-pet pixel-baby" data-type="ocean" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p3"></span><span></span><span class="p3"></span><span class="p1"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p2"></span></div>
        </div>
      `;
    }

    if (stage === 'teen') {
      return `
        <div class="pixel-pet pixel-teen" data-type="ocean" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p3"></span><span></span><span class="p3"></span><span class="p1"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p2"></span></div>
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span></span></div>
        </div>
      `;
    }

    if (stage === 'adult') {
      return `
        <div class="pixel-pet pixel-adult" data-type="ocean" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p3"></span><span></span><span></span><span class="p3"></span><span class="p1"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p2"></span></div>
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span></span></div>
        </div>
      `;
    }

    // master
    return `
      <div class="pixel-pet pixel-master" data-type="ocean" style="--pet-color: ${config.color};">
        <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p3"></span><span></span><span></span><span class="p3"></span><span class="p1"></span><span class="p2"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span></div>
        <div class="pixel-row"><span></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span></span></div>
        <div class="pixel-row"><span></span><span></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span></span><span></span></div>
        <div class="pixel-row"><span></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
        <div class="pixel-row"><span class="p2"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p2"></span></div>
        <div class="pixel-row"><span></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span></span></div>
      </div>
    `;
  }

  // 仙系精灵（神秘精灵）
  private generateFairyPet(stage: EvolutionStage, config: any): string {
    if (stage === 'egg') {
      return `
        <div class="pixel-pet pixel-egg" data-type="fairy" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span></span></div>
        </div>
      `;
    }

    if (stage === 'baby') {
      return `
        <div class="pixel-pet pixel-baby" data-type="fairy" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span></span><span class="p1"></span><span></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span></span></div>
        </div>
      `;
    }

    if (stage === 'teen') {
      return `
        <div class="pixel-pet pixel-teen" data-type="fairy" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span class="p1"></span><span></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span></span><span class="p1"></span><span></span><span class="p2"></span><span class="p3"></span></div>
          <div class="pixel-row"><span class="p1"></span><span></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span></span><span class="p1"></span></div>
          <div class="pixel-row"><span></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span></span></div>
        </div>
      `;
    }

    if (stage === 'adult') {
      return `
        <div class="pixel-pet pixel-adult" data-type="fairy" style="--pet-color: ${config.color};">
          <div class="pixel-row"><span class="p1"></span><span></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span></span></div>
          <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span></span></div>
          <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span></span><span class="p1"></span><span></span><span class="p2"></span><span class="p3"></span><span class="p1"></span></div>
          <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span><span></span></div>
          <div class="pixel-row"><span></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span></span><span class="p2"></span></div>
          <div class="pixel-row"><span></span><span></span><span class="p2"></span><span class="p3"></span><span class="p2"></span><span></span><span class="p3"></span><span></span></div>
        </div>
      `;
    }

    // master
    return `
      <div class="pixel-pet pixel-master" data-type="fairy" style="--pet-color: ${config.color};">
        <div class="pixel-row"><span class="p1"></span><span></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span></span><span class="p1"></span></div>
        <div class="pixel-row"><span></span><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span><span></span></div>
        <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span><span class="p3"></span></div>
        <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span></div>
        <div class="pixel-row"><span class="p2"></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span class="p2"></span></div>
        <div class="pixel-row"><span class="p3"></span><span class="p2"></span><span></span><span class="p1"></span><span class="p1"></span><span></span><span class="p2"></span><span class="p3"></span></div>
        <div class="pixel-row"><span class="p1"></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span class="p3"></span><span class="p1"></span><span class="p3"></span><span class="p1"></span></div>
        <div class="pixel-row"><span></span><span class="p1"></span><span class="p3"></span><span class="p2"></span><span class="p2"></span><span class="p3"></span><span class="p1"></span><span></span></div>
        <div class="pixel-row"><span></span><span></span><span class="p2"></span><span class="p3"></span><span class="p3"></span><span class="p2"></span><span></span><span class="p3"></span></div>
      </div>
    `;
  }

  render(): void {
    console.log('Rendering...');
    const pet = this.getCurrentPet();
    console.log('Current pet:', pet);

    // 确保 type 有效
    if (!PET_CONFIG[pet.type]) {
      console.error('Invalid pet type:', pet.type);
      pet.type = 'fire';
    }

    const petConfig = PET_CONFIG[pet.type];

    const html = `
      <div class="game-container pixel-border">
        <div class="header pixel-border-bottom">
          <div class="pet-info">
            <h2 class="pet-name-display pixel-text">${pet.name}</h2>
            <div class="pet-meta">
              <span class="badge pixel-text">${this.t(`petTypes.${pet.type}`)} ${this.t('ui.series')}</span>
            </div>
          </div>
          <div class="header-right">
            <div class="coin-display pixel-text">
              <span class="coin-icon">💰</span>
              <span class="coin-amount">${this.gameState.coins}</span>
            </div>
            <button class="btn-icon pixel-btn" id="languageBtn" title="${this.t('ui.language')}" style="font-size: 18px;">🌐</button>
            <button class="btn-icon pixel-btn" id="pokedexBtn" title="${this.t('ui.pokedex')}">📖</button>
            <button class="btn-icon pixel-btn btn-reset" id="resetBtn" title="${this.t('ui.reset')}">⟲</button>
          </div>
        </div>

        <div class="left-column">
          <div class="pet-display pixel-bg" style="--pet-color: ${petConfig.color}; --pet-color-2: ${petConfig.secondColor}; position: relative;">
            <button class="switch-pet-display-btn pixel-btn" id="switchPetBtn" title="${this.t('ui.switchPet')}" style="position: absolute; top: 10px; left: 10px; z-index: 10; padding: 8px 12px; font-size: 12px; background: linear-gradient(135deg, rgba(0, 255, 170, 0.2) 0%, rgba(0, 204, 255, 0.2) 100%); border: 2px solid var(--color-primary); box-shadow: 0 0 10px rgba(0, 255, 170, 0.3);">
              🔄 ${this.t('ui.switch')}
            </button>
            <div class="pet-container">
              ${this.generatePixelPet()}
            </div>
            <div class="exp-bar pixel-border">
              <div class="exp-fill pixel-gradient" style="width: ${pet.isMaxLevel ? 100 : pet.exp % 100}%; --pet-color: ${petConfig.color};"></div>
              <span class="exp-text pixel-text">${pet.isMaxLevel ? 'MAX LEVEL' : `EXP ${pet.exp % 100}/100`}</span>
            </div>
          </div>
        </div>

        <div class="right-column">

        <div class="stats-grid">
          <div class="stat-item">
            <div class="stat-header pixel-text">
              <span>${this.t('ui.hunger')}</span>
              <span class="stat-icon">${this.getStatusEmoji(pet.hunger)}</span>
            </div>
            <div class="stat-bar">
              <div class="stat-fill" style="width: ${pet.hunger}%; background: ${this.getStatusColor(pet.hunger)}"></div>
            </div>
            <div class="stat-value pixel-text">${Math.round(pet.hunger)}%</div>
          </div>

          <div class="stat-item">
            <div class="stat-header pixel-text">
              <span>${this.t('ui.happiness')}</span>
              <span class="stat-icon">${this.getStatusEmoji(pet.happiness)}</span>
            </div>
            <div class="stat-bar">
              <div class="stat-fill" style="width: ${pet.happiness}%; background: ${this.getStatusColor(pet.happiness)}"></div>
            </div>
            <div class="stat-value pixel-text">${Math.round(pet.happiness)}%</div>
          </div>

          <div class="stat-item">
            <div class="stat-header pixel-text">
              <span>${this.t('ui.cleanliness')}</span>
              <span class="stat-icon">${this.getStatusEmoji(pet.cleanliness)}</span>
            </div>
            <div class="stat-bar">
              <div class="stat-fill" style="width: ${pet.cleanliness}%; background: ${this.getStatusColor(pet.cleanliness)}"></div>
            </div>
            <div class="stat-value pixel-text">${Math.round(pet.cleanliness)}%</div>
          </div>

          <div class="stat-item">
            <div class="stat-header pixel-text">
              <span>${this.t('ui.energy')}</span>
              <span class="stat-icon">${this.getStatusEmoji(pet.energy)}</span>
            </div>
            <div class="stat-bar">
              <div class="stat-fill" style="width: ${pet.energy}%; background: ${this.getStatusColor(pet.energy)}"></div>
            </div>
            <div class="stat-value pixel-text">${Math.round(pet.energy)}%</div>
          </div>
        </div>

        <div class="actions">
          <button class="action-btn pixel-btn pixel-text" id="feedBtn">
            <span class="action-icon">🍖</span>
            <span>${this.t('ui.feed')}</span>
          </button>
          <button class="action-btn pixel-btn pixel-text" id="playBtn">
            <span class="action-icon">⚽</span>
            <span>${this.t('ui.play')}</span>
          </button>
          <button class="action-btn pixel-btn pixel-text" id="cleanBtn">
            <span class="action-icon">🧼</span>
            <span>${this.t('ui.clean')}</span>
          </button>
          <button class="action-btn pixel-btn pixel-text" id="sleepBtn">
            <span class="action-icon">💤</span>
            <span>${this.t('ui.sleep')}</span>
          </button>
        </div>

        <button class="pet-manager-btn pixel-btn" id="openPetManagerBtn">
          <span class="manager-icon">📋</span>
          <span>${this.t('ui.petManagement')} (${this.gameState.pets.length}/${this.gameState.unlockedSlots})</span>
        </button>

      </div>
      </div>
    `;

    this.container.innerHTML = html;
    console.log('HTML updated');
    this.bindEvents();
  }

  // 生成精灵管理弹窗HTML
  private generatePetManagerModal(): string {
    const totalWorkReward = this.calculateTotalWorkReward();
    const canUnlockSlot = this.gameState.unlockedSlots < MAX_PETS;
    const unlockPrice = SLOT_UNLOCK_PRICE * this.gameState.unlockedSlots;

    return `
      <div class="modal-overlay" id="petManagerModal">
        <div class="modal-content pixel-border">
          <div class="modal-header pixel-border-bottom">
            <h3 class="pixel-text">${this.t('ui.petManagement')}</h3>
            <button class="modal-close pixel-btn" id="closePetManagerBtn">✕</button>
          </div>

          <div class="modal-body">
            <!-- 打工收益总览和解锁槽位 -->
            <div class="modal-section work-summary">
              <div class="work-summary-content pixel-border">
                <div class="work-summary-left">
                  <div class="work-summary-label pixel-text">${this.t('ui.totalReward')}</div>
                  <div class="work-summary-amount pixel-text">💰 ${totalWorkReward} ${this.t('ui.coins')}</div>
                  <div class="work-summary-total pixel-text">${this.t('ui.coins')}: ${this.gameState.coins + totalWorkReward}</div>
                </div>
                <div class="work-summary-actions">
                  <button class="collect-all-btn pixel-btn" id="collectAllBtn" ${totalWorkReward === 0 ? 'disabled' : ''}>
                    <span class="collect-icon">💰</span>
                    <span>${this.t('ui.collectAll')}</span>
                  </button>
                  ${canUnlockSlot ? `
                    <button class="unlock-slot-btn-modal pixel-btn" id="unlockSlotBtnModal" ${this.gameState.coins + totalWorkReward < unlockPrice ? 'disabled' : ''}>
                      <span class="unlock-icon">🔓</span>
                      <span>${this.t('ui.unlockSlot')} (${unlockPrice}💰)</span>
                    </button>
                  ` : ''}
                </div>
              </div>
            </div>

            <!-- 合成按钮 -->
            ${(() => {
              const breedablePets = this.gameState.pets.filter(p =>
                p.happiness >= 80 && p.energy >= 50
              );
              const hasSlot = this.gameState.pets.length < this.gameState.unlockedSlots;

              // 显示合成按钮：如果有至少2只可合成的精灵并且有空闲槽位
              // 或者显示提示信息
              if (breedablePets.length >= 2 && hasSlot) {
                return `
                  <div class="modal-section">
                    <button class="breed-action-btn pixel-btn" id="openBreedSelectionBtn">
                      ${this.t('ui.breedButton')}
                    </button>
                  </div>
                `;
              } else if (breedablePets.length >= 2 && !hasSlot) {
                return `
                  <div class="modal-section">
                    <div class="breed-hint pixel-text">${this.t('ui.breedRequirement')}</div>
                  </div>
                `;
              } else if (breedablePets.length < 2) {
                const adultsNeeded = 2 - breedablePets.length;
                return `
                  <div class="modal-section">
                    <div class="breed-hint pixel-text">${this.t('ui.needMorePets').replace('{count}', adultsNeeded.toString())}</div>
                  </div>
                `;
              }
              return '';
            })()}

            <!-- 精灵列表 -->
            <div class="modal-section">
              <div class="section-title pixel-text">${this.t('ui.myPetsList')} (${this.gameState.pets.length}/${this.gameState.unlockedSlots})</div>
              <div class="pet-grid">
                ${this.getGroupedPets().map((group) => {
                  const representativePet = group.pets[0]; // 使用第一只作为代表显示
                  const totalWorkReward = group.pets.reduce((sum, p) => sum + this.calculateWorkReward(p), 0);
                  const workingCount = group.pets.filter(p => p.isWorking === true).length;
                  const idleCount = group.count - workingCount;

                  return `
                  <div class="pet-card-container">
                    <button class="pet-card grouped-card pixel-btn" data-pet-type="${group.type}">
                      <div class="card-header" style="background: ${PET_CONFIG[group.type].color}">
                        <span class="card-type">${this.t(`petTypes.${group.type}`)}</span>
                        ${group.count > 1 ? `<span class="count-badge">×${group.count}</span>` : ''}
                      </div>
                      <div class="card-body">
                        <div class="card-name">${representativePet.name}</div>
                        ${totalWorkReward > 0 ? `
                          <div class="card-work-info">
                            <span class="work-earning">${this.t('ui.totalEarnings')}: +${totalWorkReward}💰</span>
                          </div>
                        ` : ''}
                        <div class="card-status-summary">
                          ${workingCount > 0 ? `<span class="status-item">💼 ${workingCount}</span>` : ''}
                          ${idleCount > 0 ? `<span class="status-item">😴 ${idleCount}</span>` : ''}
                        </div>
                      </div>
                    </button>
                    <button class="expand-group-btn pixel-btn" data-expand-type="${group.type}" title="${this.t('ui.expand')}">
                      <span>▼</span>
                    </button>
                  </div>
                `;
                }).join('')}
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  // 显示精灵管理弹窗
  private showPetManager(): void {
    const existingModal = document.getElementById('petManagerModal');
    if (existingModal) {
      existingModal.remove();
    }

    const modalHtml = this.generatePetManagerModal();
    document.body.insertAdjacentHTML('beforeend', modalHtml);

    // 绑定弹窗内的事件
    const modal = document.getElementById('petManagerModal');
    const closeBtn = document.getElementById('closePetManagerBtn');
    const collectAllBtn = document.getElementById('collectAllBtn');
    const unlockSlotBtnModal = document.getElementById('unlockSlotBtnModal');

    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closePetManager());
    }

    if (modal) {
      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          this.closePetManager();
        }
      });
    }

    // 精灵分组卡片点击 - 切换到该类型的第一只精灵
    const petCards = document.querySelectorAll('.pet-card[data-pet-type]');
    petCards.forEach(card => {
      card.addEventListener('click', () => {
        const petType = (card as HTMLElement).dataset.petType as PetType;
        // 找到该类型的第一只精灵
        const firstPetIndex = this.gameState.pets.findIndex(p => p.type === petType);
        if (firstPetIndex !== -1) {
          this.switchPet(firstPetIndex);
          this.closePetManager();
        }
      });
    });

    // 展开按钮 - 显示该类型所有精灵的详细列表
    const expandButtons = document.querySelectorAll('.expand-group-btn');
    expandButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const petType = (btn as HTMLElement).dataset.expandType as PetType;
        this.showPetTypeDetail(petType);
      });
    });

    // 收取全部按钮
    if (collectAllBtn) {
      collectAllBtn.addEventListener('click', () => {
        this.collectAllWorkRewards();
        // 短暂延迟后重新显示弹窗以更新数据
        setTimeout(() => {
          this.closePetManager();
          setTimeout(() => this.showPetManager(), 50);
        }, 100);
      });
    }

    // 解锁槽位按钮
    if (unlockSlotBtnModal) {
      unlockSlotBtnModal.addEventListener('click', () => {
        this.unlockSlotInModal();
        // 重新显示弹窗以更新状态
        setTimeout(() => {
          this.closePetManager();
          setTimeout(() => this.showPetManager(), 50);
        }, 100);
      });
    }

    // 合成按钮
    const openBreedSelectionBtn = document.getElementById('openBreedSelectionBtn');
    if (openBreedSelectionBtn) {
      openBreedSelectionBtn.addEventListener('click', () => {
        this.closePetManager();
        setTimeout(() => this.showBreedSelection(), 100);
      });
    }

    // 放生按钮
    const releaseButtons = document.querySelectorAll('.release-pet-btn');
    releaseButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation(); // 阻止事件冒泡
        const petId = (btn as HTMLElement).dataset.releasePet!;
        this.releasePet(petId);
      });
    });

    // 显示动画
    setTimeout(() => {
      modal?.classList.add('show');
    }, 10);
  }

  // 关闭精灵管理弹窗
  private closePetManager(): void {
    const modal = document.getElementById('petManagerModal');
    if (modal) {
      modal.classList.remove('show');
      setTimeout(() => modal.remove(), 300);
    }
  }

  // 显示某类型精灵的详细列表
  private showPetTypeDetail(petType: PetType): void {
    const petsOfType = this.gameState.pets.filter(p => p.type === petType);

    const detailHtml = `
      <div class="modal-overlay" id="petTypeDetailModal" style="z-index: 9999;">
        <div class="modal-content pixel-border" style="max-width: 600px;">
          <div class="modal-header pixel-border-bottom">
            <h3 class="pixel-text">${this.t(`petTypes.${petType}`)} ${this.t('ui.series')} (${petsOfType.length})</h3>
            <button class="modal-close pixel-btn" id="closePetTypeDetailBtn">✕</button>
          </div>
          <div class="modal-body">
            <div class="pet-detail-list">
              ${petsOfType.map(pet => {
                const index = this.gameState.pets.findIndex(p => p.id === pet.id);
                const workReward = this.calculateWorkReward(pet);
                const isWorking = pet.isWorking === true;
                const isCurrent = index === this.gameState.currentPetIndex;
                const isProtected = pet.isProtected === true;

                return `
                  <div class="pet-detail-item pixel-border ${isCurrent ? 'current-pet' : ''} ${isProtected ? 'protected-pet' : ''}" data-pet-index="${index}">
                    <div class="pet-detail-info">
                      <div class="pet-detail-name">
                        ${pet.name} ${isCurrent ? '⭐' : ''} ${isProtected ? '🛡️' : ''}
                      </div>
                      <div class="pet-detail-status">
                        ${isWorking ? `
                          <span class="status-working">💼 ${this.t('ui.working')} (+${workReward}💰)</span>
                        ` : `
                          <span class="status-idle">😴 ${this.t('ui.resting')}</span>
                        `}
                      </div>
                    </div>
                    <div class="pet-detail-actions">
                      <button class="switch-pet-btn pixel-btn" data-switch-index="${index}">${this.t('ui.switch')}</button>
                      ${isProtected ? `
                        <button class="protected-badge pixel-btn" disabled title="${this.t('ui.protected')}">🛡️</button>
                      ` : `
                        <button class="release-pet-btn pixel-btn" data-release-pet="${pet.id}">🕊️</button>
                      `}
                    </div>
                  </div>
                `;
              }).join('')}
            </div>
          </div>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', detailHtml);

    const modal = document.getElementById('petTypeDetailModal');
    const closeBtn = document.getElementById('closePetTypeDetailBtn');

    const closeDetail = () => {
      modal?.classList.remove('show');
      setTimeout(() => modal?.remove(), 300);
    };

    closeBtn?.addEventListener('click', closeDetail);
    modal?.addEventListener('click', (e) => {
      if (e.target === modal) closeDetail();
    });

    // 切换按钮
    const switchButtons = document.querySelectorAll('.switch-pet-btn');
    switchButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const index = parseInt((btn as HTMLElement).dataset.switchIndex || '0');
        this.switchPet(index);
        closeDetail();
        this.closePetManager();
      });
    });

    // 放生按钮
    const releaseButtons = document.querySelectorAll('#petTypeDetailModal .release-pet-btn');
    releaseButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const petId = (btn as HTMLElement).dataset.releasePet!;
        closeDetail();
        this.releasePet(petId);
      });
    });

    setTimeout(() => modal?.classList.add('show'), 10);
  }

  // 生成图鉴弹窗HTML
  private generatePokedexModal(): string {
    const allTypes: PetType[] = [
      // 创世四元素
      'fire', 'water', 'earth', 'air',
      // 一代合成
      'steam', 'lava', 'lightning', 'swamp', 'frost', 'dust', 'blaze', 'ocean',
      // 二代合成
      'volcano', 'aurora', 'tide', 'plasma', 'snowstorm', 'typhoon', 'inferno', 'mudstone',
      'geyser', 'thunderstorm', 'glacier', 'wildfire', 'reef', 'sandstorm', 'haze', 'magma',
      // 稀有混沌系
      'fairy'
    ];
    const unlockedTypes = this.gameState.unlockedPetTypes || [];
    const unlockedCount = unlockedTypes.length;
    const totalCount = allTypes.length;

    return `
      <div class="modal-overlay" id="pokedexModal">
        <div class="modal-content pixel-border">
          <div class="modal-header pixel-border-bottom">
            <h3 class="pixel-text">${this.t('ui.pokedex')}</h3>
            <button class="modal-close pixel-btn" id="closePokedexBtn">✕</button>
          </div>

          <div class="modal-body">
            <div class="pokedex-stats pixel-border">
              <div class="pokedex-progress pixel-text">
                <span class="progress-label">${this.t('ui.unlockProgress')}</span>
                <span class="progress-count">${unlockedCount}/${totalCount}</span>
              </div>
              <div class="progress-bar pixel-border">
                <div class="progress-fill" style="width: ${(unlockedCount / totalCount * 100).toFixed(1)}%"></div>
              </div>
            </div>

            <div class="pokedex-grid">
              ${allTypes.map(type => {
                const isUnlocked = unlockedTypes.includes(type);
                const config = PET_CONFIG[type];

                return `
                  <div class="pokedex-card ${isUnlocked ? 'unlocked' : 'locked'} pixel-border">
                    <div class="pokedex-card-header" style="background: ${isUnlocked ? config.color : '#333'}">
                      ${isUnlocked ? this.t(`petTypes.${type}`) : '???'}
                    </div>
                    <div class="pokedex-card-body" style="background: ${isUnlocked ? `linear-gradient(135deg, ${config.color}40 0%, ${config.secondColor}40 100%)` : '#1a1a1a'}">
                      ${isUnlocked ? `
                        <div class="pokedex-icon" style="color: ${config.color}">
                          ${this.getTypeIcon(type)}
                        </div>
                        <div class="pokedex-type pixel-text">${this.t(`petTypes.${type}`)}</div>
                      ` : `
                        <div class="pokedex-locked">
                          <div class="lock-icon">🔒</div>
                          <div class="lock-text pixel-text">${this.t('ui.locked')}</div>
                        </div>
                      `}
                    </div>
                  </div>
                `;
              }).join('')}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  // 获取类型图标
  private getTypeIcon(type: PetType): string {
    const icons: Record<PetType, string> = {
      fire: '🔥',
      water: '💧',
      earth: '🌍',
      air: '💨',
      steam: '♨️',
      lava: '🌋',
      lightning: '⚡',
      swamp: '🐸',
      frost: '❄️',
      dust: '🌪️',
      blaze: '🔥',
      ocean: '🌊',
      fairy: '✨',
    };
    return icons[type] || '❓';
  }

  // 显示图鉴弹窗
  private showPokedex(): void {
    const existingModal = document.getElementById('pokedexModal');
    if (existingModal) {
      existingModal.remove();
    }

    const modalHtml = this.generatePokedexModal();
    document.body.insertAdjacentHTML('beforeend', modalHtml);

    // 绑定弹窗内的事件
    const modal = document.getElementById('pokedexModal');
    const closeBtn = document.getElementById('closePokedexBtn');

    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closePokedex());
    }

    if (modal) {
      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          this.closePokedex();
        }
      });
    }

    // 显示动画
    setTimeout(() => {
      modal?.classList.add('show');
    }, 10);
  }

  // 关闭图鉴弹窗
  private closePokedex(): void {
    const modal = document.getElementById('pokedexModal');
    if (modal) {
      modal.classList.remove('show');
      setTimeout(() => modal.remove(), 300);
    }
  }

  // 显示精灵切换弹窗
  private showPetSwitcher(): void {
    const existingModal = document.getElementById('petSwitcherModal');
    if (existingModal) {
      existingModal.remove();
    }

    const currentPet = this.getCurrentPet();

    // 按类型分组精灵，每个类型只显示第一个
    const petsByType = new Map<PetType, {pet: PetState, index: number}>();
    this.gameState.pets.forEach((pet, index) => {
      if (!petsByType.has(pet.type)) {
        petsByType.set(pet.type, {pet, index});
      }
    });

    // 转换为数组并保持原始索引
    const uniquePets = Array.from(petsByType.values());

    const modalHtml = `
      <div class="modal-overlay" id="petSwitcherModal">
        <div class="modal-content pixel-border" style="max-width: 600px;">
          <div class="modal-header pixel-border-bottom">
            <h2 class="pixel-text">${this.t('ui.switchPetTitle')}</h2>
            <button class="modal-close pixel-btn" id="closePetSwitcherBtn">✕</button>
          </div>
          <div class="modal-body" style="padding: 20px;">
            <div class="pet-switcher-grid" style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; max-height: 60vh; overflow-y: auto;">
              ${uniquePets.map(({pet, index}) => {
                const petConfig = PET_CONFIG[pet.type];
                const isCurrent = index === this.gameState.currentPetIndex;
                return `
                  <div class="switcher-card pixel-border ${isCurrent ? 'current-switcher' : ''}"
                       data-pet-index="${index}"
                       style="
                         padding: 15px;
                         background: linear-gradient(135deg, ${petConfig.color}22 0%, ${petConfig.secondColor}22 100%);
                         border: 3px solid ${isCurrent ? petConfig.color : 'var(--color-border)'};
                         cursor: ${isCurrent ? 'default' : 'pointer'};
                         transition: all 0.3s ease;
                         position: relative;
                       ">
                    ${isCurrent ? '<div style="position: absolute; top: 8px; right: 8px; font-size: 20px;">⭐</div>' : ''}
                    <div style="text-align: center; margin-bottom: 10px; font-size: 18px; color: ${petConfig.color}; font-weight: bold;">
                      ${pet.name}
                    </div>
                    <div style="text-align: center; margin-bottom: 8px; font-size: 13px; color: #aaa;">
                      ${this.t(`petTypes.${pet.type}`)} ${this.t('ui.series')}
                    </div>
                    <div style="display: flex; justify-content: space-around; font-size: 11px; color: #ccc;">
                      <span>😊 ${Math.round(pet.happiness)}</span>
                      <span>⚡ ${Math.round(pet.energy)}</span>
                    </div>
                  </div>
                `;
              }).join('')}
            </div>
          </div>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHtml);

    const modal = document.getElementById('petSwitcherModal');
    const closeBtn = document.getElementById('closePetSwitcherBtn');

    const closeModal = () => {
      modal?.classList.remove('show');
      setTimeout(() => modal?.remove(), 300);
    };

    closeBtn?.addEventListener('click', closeModal);
    modal?.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });

    // 绑定卡片点击事件
    const cards = document.querySelectorAll('.switcher-card');
    cards.forEach(card => {
      card.addEventListener('click', () => {
        const index = parseInt((card as HTMLElement).dataset.petIndex || '0');
        if (index !== this.gameState.currentPetIndex) {
          this.switchPet(index);
          closeModal();
        }
      });

      // hover 效果
      if (!card.classList.contains('current-switcher')) {
        card.addEventListener('mouseenter', () => {
          (card as HTMLElement).style.transform = 'translateY(-4px)';
          (card as HTMLElement).style.boxShadow = '0 8px 20px rgba(0, 255, 170, 0.4)';
        });
        card.addEventListener('mouseleave', () => {
          (card as HTMLElement).style.transform = 'translateY(0)';
          (card as HTMLElement).style.boxShadow = '';
        });
      }
    });

    // 显示动画
    setTimeout(() => {
      modal?.classList.add('show');
    }, 10);
  }

  private showLanguageSelector(): void {
    const existingModal = document.getElementById('languageSelectorModal');
    if (existingModal) {
      existingModal.remove();
    }

    const modalHtml = `
      <div class="modal-overlay" id="languageSelectorModal">
        <div class="modal-content pixel-border" style="max-width: 400px;">
          <div class="modal-header pixel-border-bottom">
            <h2 class="pixel-text">${this.t('ui.selectLanguage')}</h2>
            <button class="modal-close pixel-btn" id="closeLanguageBtn">✕</button>
          </div>
          <div class="modal-body" style="padding: 20px;">
            <div style="display: flex; flex-direction: column; gap: 12px;">
              <button class="language-option pixel-btn ${this.currentLanguage === 'en' ? 'selected' : ''}"
                      data-lang="en"
                      style="
                        padding: 16px;
                        font-size: 18px;
                        background: ${this.currentLanguage === 'en' ? 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)' : 'rgba(255, 255, 255, 0.1)'};
                        border: 2px solid ${this.currentLanguage === 'en' ? 'var(--color-primary)' : 'var(--color-border)'};
                        cursor: pointer;
                        transition: all 0.3s ease;
                      ">
                🇺🇸 English
              </button>
              <button class="language-option pixel-btn ${this.currentLanguage === 'zh' ? 'selected' : ''}"
                      data-lang="zh"
                      style="
                        padding: 16px;
                        font-size: 18px;
                        background: ${this.currentLanguage === 'zh' ? 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)' : 'rgba(255, 255, 255, 0.1)'};
                        border: 2px solid ${this.currentLanguage === 'zh' ? 'var(--color-primary)' : 'var(--color-border)'};
                        cursor: pointer;
                        transition: all 0.3s ease;
                      ">
                🇨🇳 中文
              </button>
            </div>
          </div>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHtml);

    const modal = document.getElementById('languageSelectorModal');
    const closeBtn = document.getElementById('closeLanguageBtn');

    const closeModal = () => {
      modal?.classList.remove('show');
      setTimeout(() => modal?.remove(), 300);
    };

    closeBtn?.addEventListener('click', closeModal);
    modal?.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });

    // Bind language option clicks
    const languageOptions = document.querySelectorAll('.language-option');
    languageOptions.forEach(option => {
      option.addEventListener('click', () => {
        const lang = (option as HTMLElement).dataset.lang as Language;
        if (lang && lang !== this.currentLanguage) {
          this.switchLanguage(lang);
          closeModal();
        }
      });
    });

    setTimeout(() => {
      modal?.classList.add('show');
    }, 10);
  }

  // 生成合成选择界面 - 下拉选择式
  private generateBreedSelectionModal(): string {
    // 所有精灵都可以合成(除了混沌系,混沌系只能通过概率获得)
    const elementalTypes: PetType[] = ['fire', 'water', 'earth', 'air'];
    const firstGenTypes: PetType[] = ['steam', 'lava', 'lightning', 'swamp', 'frost', 'dust', 'blaze', 'ocean'];
    const secondGenTypes: PetType[] = ['volcano', 'aurora', 'tide', 'plasma', 'snowstorm', 'typhoon', 'inferno', 'mudstone', 'geyser', 'thunderstorm', 'glacier', 'wildfire', 'reef', 'sandstorm', 'haze', 'magma'];
    const breedableTypes = [...elementalTypes, ...firstGenTypes, ...secondGenTypes];

    const breedablePets = this.gameState.pets.filter(p =>
      breedableTypes.includes(p.type) &&
      p.happiness >= 80 &&
      p.energy >= 50
    );

    // 按类型排序
    const typeOrder: PetType[] = [
      'fire', 'water', 'earth', 'air',
      'steam', 'lava', 'lightning', 'swamp', 'frost', 'dust', 'blaze', 'ocean',
      'volcano', 'aurora', 'tide', 'plasma', 'snowstorm', 'typhoon', 'inferno', 'mudstone',
      'geyser', 'thunderstorm', 'glacier', 'wildfire', 'reef', 'sandstorm', 'haze', 'magma'
    ];
    const sortedBreedablePets = [...breedablePets].sort((a, b) => {
      const aIndex = typeOrder.indexOf(a.type);
      const bIndex = typeOrder.indexOf(b.type);
      return aIndex - bIndex;
    });

    // 生成选项HTML
    const generateOptions = () => {
      return sortedBreedablePets.map(p => `
        <option value="${p.id}" data-type="${p.type}">
          ${this.t(`petTypes.${p.type}`)} ${this.t('ui.series')} - ${p.name} (😊${Math.round(p.happiness)} ⚡${Math.round(p.energy)})
        </option>
      `).join('');
    };

    return `
      <div class="modal-overlay" id="breedSelectionModal">
        <div class="modal-content pixel-border" style="max-width: 600px;">
          <div class="modal-header pixel-border-bottom">
            <h3 class="pixel-text">${this.t('ui.selectBreedPets')}</h3>
            <button class="modal-close pixel-btn" id="closeBreedSelectionBtn">✕</button>
          </div>
          <div class="breed-requirement-notice">
            <p>${this.t('ui.breedRequirement')}</p>
          </div>
          <div class="modal-body" style="padding: 30px 20px;">
            <div class="breed-selector-container">
              <div class="breed-selector-row">
                <div class="breed-selector-item">
                  <label class="breed-selector-label">${this.t('ui.pet1')}</label>
                  <div class="breed-selector-wrapper">
                    <select class="breed-selector pixel-btn" id="breedSelect1">
                      <option value="">${this.t('ui.pleaseSelect')}</option>
                      ${generateOptions()}
                    </select>
                    <div class="breed-preview" id="breedPreview1">
                      <span class="preview-placeholder">${this.t('ui.notSelected')}</span>
                    </div>
                  </div>
                </div>

                <div class="breed-plus-icon">
                  <span>+</span>
                </div>

                <div class="breed-selector-item">
                  <label class="breed-selector-label">${this.t('ui.pet2')}</label>
                  <div class="breed-selector-wrapper">
                    <select class="breed-selector pixel-btn" id="breedSelect2">
                      <option value="">${this.t('ui.pleaseSelect')}</option>
                      ${generateOptions()}
                    </select>
                    <div class="breed-preview" id="breedPreview2">
                      <span class="preview-placeholder">${this.t('ui.notSelected')}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="breed-confirm-section" style="margin-top: 30px; text-align: center;">
                <button class="breed-confirm-btn pixel-btn" id="confirmBreedBtn" disabled>
                  ${this.t('ui.breed')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  // 显示合成选择界面
  private showBreedSelection(): void {
    const existingModal = document.getElementById('breedSelectionModal');
    if (existingModal) {
      existingModal.remove();
    }

    const modalHtml = this.generateBreedSelectionModal();
    document.body.insertAdjacentHTML('beforeend', modalHtml);

    const modal = document.getElementById('breedSelectionModal');
    const closeBtn = document.getElementById('closeBreedSelectionBtn');
    const confirmBtn = document.getElementById('confirmBreedBtn');
    const select1 = document.getElementById('breedSelect1') as HTMLSelectElement;
    const select2 = document.getElementById('breedSelect2') as HTMLSelectElement;
    const preview1 = document.getElementById('breedPreview1');
    const preview2 = document.getElementById('breedPreview2');

    // 更新选项禁用状态
    const updateOptionStates = () => {
      const pet1Id = select1.value;
      const pet2Id = select2.value;

      // 更新 select1 的选项
      Array.from(select1.options).forEach(option => {
        if (option.value && option.value === pet2Id) {
          option.disabled = true;
          option.text = option.text.replace(' ✓', '') + ' ✓';
        } else {
          option.disabled = false;
          option.text = option.text.replace(' ✓', '');
        }
      });

      // 更新 select2 的选项
      Array.from(select2.options).forEach(option => {
        if (option.value && option.value === pet1Id) {
          option.disabled = true;
          option.text = option.text.replace(' ✓', '') + ' ✓';
        } else {
          option.disabled = false;
          option.text = option.text.replace(' ✓', '');
        }
      });
    };

    // 更新预览
    const updatePreview = (selectElement: HTMLSelectElement, previewElement: HTMLElement | null) => {
      const selectedOption = selectElement.options[selectElement.selectedIndex];
      const petId = selectedOption.value;

      if (petId && previewElement) {
        const petType = selectedOption.dataset.type as PetType;
        const petConfig = PET_CONFIG[petType];
        previewElement.innerHTML = `
          <div style="display: inline-block; padding: 6px 12px; background: ${petConfig.color}; border-radius: 4px;">
            <span style="color: #000; font-weight: bold;">${this.t(`petTypes.${petType}`)}${this.t('ui.series')}</span>
          </div>
        `;
      } else if (previewElement) {
        previewElement.innerHTML = `<span class="preview-placeholder">${this.t('ui.notSelected')}</span>`;
      }
    };

    // 检查是否可以合成
    const checkCanBreed = () => {
      const pet1Id = select1.value;
      const pet2Id = select2.value;

      if (confirmBtn) {
        if (pet1Id && pet2Id && pet1Id !== pet2Id) {
          confirmBtn.removeAttribute('disabled');
        } else {
          confirmBtn.setAttribute('disabled', 'true');
        }
      }
    };

    // 绑定下拉选择事件
    if (select1) {
      select1.addEventListener('change', () => {
        updatePreview(select1, preview1);
        updateOptionStates();
        checkCanBreed();
      });
    }

    if (select2) {
      select2.addEventListener('change', () => {
        updatePreview(select2, preview2);
        updateOptionStates();
        checkCanBreed();
      });
    }

    // 确认合成按钮
    if (confirmBtn) {
      confirmBtn.addEventListener('click', () => {
        const pet1Id = select1.value;
        const pet2Id = select2.value;

        if (pet1Id && pet2Id && pet1Id !== pet2Id) {
          this.breedTwoPets(pet1Id, pet2Id);
          this.closeBreedSelection();
          // 重新打开精灵管理界面以显示新精灵
          setTimeout(() => this.showPetManager(), 100);
        }
      });
    }

    // 关闭按钮
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closeBreedSelection());
    }

    if (modal) {
      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          this.closeBreedSelection();
        }
      });
    }

    // 显示动画
    setTimeout(() => {
      modal?.classList.add('show');
    }, 10);
  }

  // 关闭合成选择界面
  private closeBreedSelection(): void {
    const modal = document.getElementById('breedSelectionModal');
    if (modal) {
      modal.classList.remove('show');
      setTimeout(() => modal.remove(), 300);
    }
  }

  // 两只精灵合成
  private breedTwoPets(petId1: string, petId2: string): void {
    const pet1 = this.gameState.pets.find(p => p.id === petId1);
    const pet2 = this.gameState.pets.find(p => p.id === petId2);

    if (!pet1 || !pet2) return;

    // 检查槽位
    if (this.gameState.pets.length >= this.gameState.unlockedSlots) {
      this.showNotification(this.t('ui.noti.noSlot'));
      return;
    }

    // 根据两只精灵的类型决定后代类型
    const breedKey = `${pet1.type}-${pet2.type}`;
    const reverseKey = `${pet2.type}-${pet1.type}`;
    let childType = BREED_COMBINATIONS[breedKey] || BREED_COMBINATIONS[reverseKey] || pet1.type;

    // 检查是否是二代精灵合成
    const secondGenTypes: PetType[] = ['volcano', 'aurora', 'tide', 'plasma', 'snowstorm', 'typhoon', 'inferno', 'mudstone', 'geyser', 'thunderstorm', 'glacier', 'wildfire', 'reef', 'sandstorm', 'haze', 'magma'];
    const isPet1SecondGen = secondGenTypes.includes(pet1.type);
    const isPet2SecondGen = secondGenTypes.includes(pet2.type);

    // 只有两只都是二代精灵时才有5%概率产生混沌系
    let isFairy = false;
    if (isPet1SecondGen && isPet2SecondGen && Math.random() < FAIRY_HATCH_RATE) {
      childType = 'fairy';
      isFairy = true;
    }

    // 创建新精灵(固定1星)
    const newPet = this.createNewPet(childType);
    this.gameState.pets.push(newPet);

    // 解锁新类型到图鉴
    if (!this.gameState.unlockedPetTypes) {
      this.gameState.unlockedPetTypes = [];
    }
    if (!this.gameState.unlockedPetTypes.includes(newPet.type)) {
      this.gameState.unlockedPetTypes.push(newPet.type);
    }

    // 合成消耗状态（四元素精灵不消耗）
    const isElemental1 = ['fire', 'water', 'earth', 'air'].includes(pet1.type);
    const isElemental2 = ['fire', 'water', 'earth', 'air'].includes(pet2.type);

    if (!isElemental1) {
      pet1.energy = Math.max(0, pet1.energy - 30);
      pet1.happiness = Math.max(0, pet1.happiness - 20);
    }

    if (!isElemental2) {
      pet2.energy = Math.max(0, pet2.energy - 30);
      pet2.happiness = Math.max(0, pet2.happiness - 20);
    }

    this.saveGameState();
    this.updateDisplay();

    // 显示合成成功弹窗
    if (isFairy) {
      this.showFairyCelebration();
    } else {
      this.showBreedSuccessModal(newPet);
    }
  }

  // 显示合成成功弹窗
  private showBreedSuccessModal(newPet: PetState): void {
    const petConfig = PET_CONFIG[newPet.type];

    const modalHtml = `
      <div class="modal-overlay breed-success-modal" id="breedSuccessModal" style="z-index: 100000;">
        <div class="modal-content pixel-border" style="max-width: 500px;">
          <div class="modal-header pixel-border-bottom">
            <h2 class="pixel-text" style="color: #ffaa00; text-align: center; font-size: 20px;">${this.t('ui.breedSuccess')}</h2>
          </div>
          <div class="modal-body" style="text-align: center; padding: 30px 20px;">
            <div class="breed-success-icon" style="font-size: 64px; margin: 15px 0;">✨</div>
            <h3 class="pixel-text" style="font-size: 18px; margin: 15px 0; color: #00ffaa;">
              ${this.t('ui.congratulations')}
            </h3>
            <div style="margin: 20px 0;">
              <div style="display: inline-block; padding: 12px 24px; background: ${petConfig.color}; border-radius: 8px; box-shadow: 0 4px 15px rgba(0,0,0,0.3);">
                <span style="font-size: 22px; font-weight: bold; color: #000;">${newPet.name}</span>
              </div>
            </div>
            <p class="pixel-text" style="font-size: 13px; line-height: 1.8; color: #ccc; margin-top: 15px;">
              ${this.t('ui.newPetJoined').replace(/\n/g, '<br>')}
            </p>
            <button class="pixel-btn" id="closeBreedSuccessBtn" style="margin-top: 25px; padding: 10px 32px; font-size: 14px; background: linear-gradient(135deg, #ffaa00 0%, #ff8800 100%); border: 2px solid #ffcc00; color: #000;">
              ${this.t('ui.awesome')}
            </button>
          </div>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHtml);

    const modal = document.getElementById('breedSuccessModal');
    const closeBtn = document.getElementById('closeBreedSuccessBtn');

    const closeModal = () => {
      if (modal) {
        modal.classList.remove('show');
        setTimeout(() => modal.remove(), 300);
      }
    };

    if (closeBtn) {
      closeBtn.addEventListener('click', closeModal);
    }

    if (modal) {
      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          closeModal();
        }
      });
    }

    // 显示动画
    setTimeout(() => {
      modal?.classList.add('show');
    }, 10);
  }

  // 显示仙系诞生庆贺弹窗
  private showFairyCelebration(): void {
    const modalHtml = `
      <div class="modal-overlay fairy-celebration-modal" id="fairyCelebrationModal">
        <div class="modal-content pixel-border">
          <div class="modal-header pixel-border-bottom">
            <h2 class="pixel-text" style="color: #ffd700; text-align: center;">✨ 奇迹诞生 ✨</h2>
          </div>
          <div class="modal-body" style="text-align: center; padding: 40px 20px;">
            <div class="fairy-celebration-icon" style="font-size: 80px; margin: 20px 0;">🧚</div>
            <h3 class="pixel-text" style="font-size: 24px; margin: 20px 0; color: #ff69b4;">
              恭喜！稀有仙系精灵诞生！
            </h3>
            <p class="pixel-text" style="font-size: 16px; line-height: 1.8; color: #ccc;">
              在您的悉心照料下，<br>
              一只传说中的仙系精灵降临了！<br>
              这是万分之一的奇迹！
            </p>
            <button class="pixel-btn" id="closeFairyCelebrationBtn" style="margin-top: 30px; padding: 12px 40px; font-size: 16px;">
              ✨ 太棒了！
            </button>
          </div>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHtml);

    const modal = document.getElementById('fairyCelebrationModal');
    const closeBtn = document.getElementById('closeFairyCelebrationBtn');

    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        if (modal) {
          modal.classList.remove('show');
          setTimeout(() => modal.remove(), 300);
        }
      });
    }

    if (modal) {
      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          modal.classList.remove('show');
          setTimeout(() => modal.remove(), 300);
        }
      });
    }

    // 显示动画
    setTimeout(() => {
      modal?.classList.add('show');
    }, 10);
  }

  private bindEvents(): void {
    console.log('Binding events...');
    const feedBtn = document.getElementById('feedBtn');
    const playBtn = document.getElementById('playBtn');
    const cleanBtn = document.getElementById('cleanBtn');
    const sleepBtn = document.getElementById('sleepBtn');
    const resetBtn = document.getElementById('resetBtn');
    const pokedexBtn = document.getElementById('pokedexBtn');
    const languageBtn = document.getElementById('languageBtn');
    const switchPetBtn = document.getElementById('switchPetBtn');
    const openPetManagerBtn = document.getElementById('openPetManagerBtn');

    if (feedBtn) feedBtn.addEventListener('click', () => this.feed());
    if (playBtn) playBtn.addEventListener('click', () => this.play());
    if (cleanBtn) cleanBtn.addEventListener('click', () => this.clean());
    if (sleepBtn) sleepBtn.addEventListener('click', () => this.sleep());
    if (resetBtn) resetBtn.addEventListener('click', () => this.showResetConfirmation());
    if (pokedexBtn) pokedexBtn.addEventListener('click', () => this.showPokedex());
    if (languageBtn) languageBtn.addEventListener('click', () => this.showLanguageSelector());
    if (switchPetBtn) switchPetBtn.addEventListener('click', () => this.showPetSwitcher());

    // 打开精灵管理弹窗
    if (openPetManagerBtn) {
      openPetManagerBtn.addEventListener('click', () => this.showPetManager());
    }

    console.log('Events bound');
  }

  private switchPet(index: number): void {
    if (index >= 0 && index < this.gameState.pets.length) {
      this.gameState.currentPetIndex = index;
      this.saveGameState();
      this.render();
    }
  }

  private addNewPet(): void {
    if (this.gameState.pets.length < this.gameState.unlockedSlots) {
      const newPet = this.createNewPet();
      this.gameState.pets.push(newPet);
      this.gameState.currentPetIndex = this.gameState.pets.length - 1;
      this.saveGameState();
      this.render();
      this.showNotification(this.t('ui.noti.newPetBorn'));
    }
  }

  // 获取排序后的精灵列表(按类型分组)
  private getSortedPets(): PetState[] {
    const typeOrder: PetType[] = [
      'fire', 'water', 'earth', 'air',
      'steam', 'lava', 'lightning', 'swamp', 'frost', 'dust', 'blaze', 'ocean',
      'volcano', 'aurora', 'tide', 'plasma', 'snowstorm', 'typhoon', 'inferno', 'mudstone',
      'geyser', 'thunderstorm', 'glacier', 'wildfire', 'reef', 'sandstorm', 'haze', 'magma',
      'fairy'
    ];

    return [...this.gameState.pets].sort((a, b) => {
      const aIndex = typeOrder.indexOf(a.type);
      const bIndex = typeOrder.indexOf(b.type);
      return aIndex - bIndex;
    });
  }

  // 获取分组后的精灵(按类型合并)
  private getGroupedPets(): Array<{ type: PetType; pets: PetState[]; count: number }> {
    const groups = new Map<PetType, PetState[]>();

    this.gameState.pets.forEach(pet => {
      if (!groups.has(pet.type)) {
        groups.set(pet.type, []);
      }
      groups.get(pet.type)!.push(pet);
    });

    const typeOrder: PetType[] = [
      'fire', 'water', 'earth', 'air',
      'steam', 'lava', 'lightning', 'swamp', 'frost', 'dust', 'blaze', 'ocean',
      'volcano', 'aurora', 'tide', 'plasma', 'snowstorm', 'typhoon', 'inferno', 'mudstone',
      'geyser', 'thunderstorm', 'glacier', 'wildfire', 'reef', 'sandstorm', 'haze', 'magma',
      'fairy'
    ];

    return Array.from(groups.entries())
      .map(([type, pets]) => ({ type, pets, count: pets.length }))
      .sort((a, b) => typeOrder.indexOf(a.type) - typeOrder.indexOf(b.type));
  }


  // 显示自定义确认对话框
  private showConfirmDialog(message: string, onConfirm: () => void): void {
    const confirmHtml = `
      <div class="modal-overlay" id="confirmDialog" style="z-index: 10000;">
        <div class="modal-content pixel-border" style="max-width: 400px;">
          <div class="modal-header pixel-border-bottom">
            <h2>⚠️ 确认操作</h2>
          </div>
          <div class="modal-body" style="padding: 30px 20px; text-align: center; white-space: pre-line;">
            ${message}
          </div>
          <div class="modal-footer" style="display: flex; gap: 10px; padding: 15px;">
            <button class="pixel-btn" id="confirmDialogCancel" style="flex: 1;">取消</button>
            <button class="pixel-btn" id="confirmDialogOk" style="flex: 1; background: #e74c3c;">确定</button>
          </div>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', confirmHtml);

    const modal = document.getElementById('confirmDialog');
    const cancelBtn = document.getElementById('confirmDialogCancel');
    const okBtn = document.getElementById('confirmDialogOk');

    const closeDialog = () => {
      modal?.classList.remove('show');
      setTimeout(() => modal?.remove(), 300);
    };

    cancelBtn?.addEventListener('click', closeDialog);
    okBtn?.addEventListener('click', () => {
      closeDialog();
      onConfirm();
    });

    // 点击背景关闭
    modal?.addEventListener('click', (e) => {
      if (e.target === modal) closeDialog();
    });

    // 显示动画
    setTimeout(() => modal?.classList.add('show'), 10);
  }

  // 放生精灵 - 三级确认
  private releasePet(petId: string): void {
    const petIndex = this.gameState.pets.findIndex(p => p.id === petId);
    if (petIndex === -1) return;

    const pet = this.gameState.pets[petIndex];

    // 检查是否受保护
    if (pet.isProtected) {
      this.showNotification(this.t('ui.noti.protected'));
      return;
    }

    // 显示三级确认对话框
    this.showReleaseConfirmation(petIndex, pet);
  }

  // 显示放生确认对话框(一次确认)
  private showReleaseConfirmation(petIndex: number, pet: PetState): void {
    const confirmHtml = `
      <div class="modal-overlay" id="releaseConfirmModal" style="z-index: 10000;">
        <div class="modal-content pixel-border" style="max-width: 500px;">
          <div class="modal-header pixel-border-bottom">
            <h2>${this.t('ui.releaseConfirm')}</h2>
            <button class="modal-close pixel-btn" id="closeReleaseConfirmBtn">✕</button>
          </div>
          <div class="modal-body" style="padding: 20px;">
            <div class="release-pet-info pixel-border" style="padding: 15px; background: rgba(0,0,0,0.3); margin-bottom: 15px;">
              <div style="font-size: 16px; margin-bottom: 8px;">
                <strong>${this.t('ui.petName')}:</strong> ${pet.name}
              </div>
              <div style="font-size: 16px; margin-bottom: 8px;">
                <strong>${this.t('ui.petType')}:</strong> ${this.t(`petTypes.${pet.type}`)} ${this.t('ui.series')}
              </div>
              <div style="font-size: 14px; color: #ff4757; margin-top: 10px;">
                ${this.t('ui.releaseWarning')}
              </div>
            </div>
            <div style="display: flex; gap: 10px;">
              <button class="pixel-btn" id="cancelRelease" style="flex: 1;">${this.t('ui.cancel')}</button>
              <button class="pixel-btn" id="confirmRelease" style="flex: 1; background: #e74c3c;">${this.t('ui.confirmRelease')}</button>
            </div>
          </div>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', confirmHtml);

    const modal = document.getElementById('releaseConfirmModal');
    const closeBtn = document.getElementById('closeReleaseConfirmBtn');

    const closeModal = () => {
      modal?.classList.remove('show');
      setTimeout(() => modal?.remove(), 300);
    };

    closeBtn?.addEventListener('click', closeModal);
    modal?.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });

    document.getElementById('cancelRelease')?.addEventListener('click', closeModal);
    document.getElementById('confirmRelease')?.addEventListener('click', () => {
      closeModal();
      this.executeRelease(petIndex, pet);
    });

    setTimeout(() => modal?.classList.add('show'), 10);
  }

  // 执行放生操作
  private executeRelease(petIndex: number, pet: PetState): void {

    // 如果是当前精灵，切换到其他精灵
    if (petIndex === this.gameState.currentPetIndex) {
      // 如果有其他精灵，切换到第一只
      if (this.gameState.pets.length > 1) {
        this.gameState.currentPetIndex = petIndex === 0 ? 0 : 0;
      }
    } else if (petIndex < this.gameState.currentPetIndex) {
      // 如果放生的精灵在当前精灵之前，调整当前索引
      this.gameState.currentPetIndex--;
    }

    // 移除精灵
    this.gameState.pets.splice(petIndex, 1);

    // 确保当前索引有效
    if (this.gameState.currentPetIndex >= this.gameState.pets.length) {
      this.gameState.currentPetIndex = Math.max(0, this.gameState.pets.length - 1);
    }

    this.saveGameState();
    this.showNotification(this.t('ui.noti.released').replace('{name}', pet.name));

    // 重新显示精灵管理界面
    this.closePetManager();
    setTimeout(() => {
      this.showPetManager();
      this.render();
    }, 100);
  }

  // 计算所有精灵的总打工收益
  private calculateTotalWorkReward(): number {
    let total = 0;
    this.gameState.pets.forEach(pet => {
      total += this.calculateWorkReward(pet);
    });
    return total;
  }

  // 收取所有精灵的打工奖励
  private collectAllWorkRewards(): void {
    let totalReward = 0;

    this.gameState.pets.forEach(pet => {
      if (pet.isWorking && pet.workStartTime) {
        const reward = this.calculateWorkReward(pet);
        totalReward += reward;
        // 重置打工开始时间为现在
        pet.workStartTime = Date.now();
      }
    });

    if (totalReward > 0) {
      this.gameState.coins += totalReward;
      this.saveGameState();
      this.render();
      this.showNotification(this.t('ui.noti.collectSuccess').replace('{amount}', totalReward.toString()));
    }
  }

  // 开始/停止打工
  private toggleWork(petId: string): void {
    const pet = this.gameState.pets.find(p => p.id === petId);
    if (!pet) return;

    if (pet.isWorking) {
      // 停止打工，收取金币
      this.collectWorkReward(petId);
    } else {
      // 开始打工
      pet.isWorking = true;
      pet.workStartTime = Date.now();
      this.saveGameState();
      this.render();
      this.showNotification(this.t('ui.noti.startWorking').replace('{name}', pet.name));
    }
  }

  // 计算打工收益
  private calculateWorkReward(pet: PetState): number {
    if (!pet.isWorking || !pet.workStartTime) return 0;

    const workTime = Date.now() - pet.workStartTime;
    const seconds = workTime / 1000; // 转换为秒

    // 每秒产生1金币
    const baseRate = 1;
    const reward = Math.floor(baseRate * seconds);

    return reward;
  }

  // 收取打工奖励
  private collectWorkReward(petId: string): void {
    const pet = this.gameState.pets.find(p => p.id === petId);
    if (!pet || !pet.isWorking) return;

    const reward = this.calculateWorkReward(pet);

    pet.isWorking = false;
    pet.workStartTime = undefined;

    if (reward > 0) {
      this.gameState.coins += reward;
      this.saveGameState();
      this.render();
      this.showNotification(this.t('ui.noti.finishWorking').replace('{name}', pet.name).replace('{amount}', reward.toString()));
    } else {
      this.saveGameState();
      this.render();
      this.showNotification(this.t('ui.noti.stopWorking').replace('{name}', pet.name));
    }
  }

  // 原有的work方法改为当前精灵快捷打工
  private work(): void {
    const pet = this.getCurrentPet();
    this.toggleWork(pet.id);
  }

  // 在弹窗中解锁新槽位(自动收取金币)
  private unlockSlotInModal(): void {
    if (this.gameState.unlockedSlots >= MAX_PETS) {
      this.showNotification(this.t('ui.noti.maxSlots'));
      return;
    }

    const price = SLOT_UNLOCK_PRICE * this.gameState.unlockedSlots;
    const totalWorkReward = this.calculateTotalWorkReward();
    const totalCoins = this.gameState.coins + totalWorkReward;

    if (totalCoins < price) {
      this.showNotification(this.t('ui.noti.notEnoughCoins').replace('{amount}', price.toString()));
      return;
    }

    // 先收取所有金币
    if (totalWorkReward > 0) {
      this.gameState.pets.forEach(pet => {
        if (pet.isWorking && pet.workStartTime) {
          pet.workStartTime = Date.now();
        }
      });
      this.gameState.coins += totalWorkReward;
    }

    // 扣除金币并解锁
    this.gameState.coins -= price;
    this.gameState.unlockedSlots += 1;

    this.saveGameState();
    this.render();
    this.showNotification(this.t('ui.noti.slotUnlocked').replace('{count}', this.gameState.unlockedSlots.toString()));
  }

  // 解锁新槽位(保留原方法以防需要)
  private unlockSlot(): void {
    if (this.gameState.unlockedSlots >= MAX_PETS) {
      this.showNotification(this.t('ui.noti.maxSlots'));
      return;
    }

    const price = SLOT_UNLOCK_PRICE * this.gameState.unlockedSlots;

    if (this.gameState.coins < price) {
      this.showNotification(this.t('ui.noti.notEnoughCoins').replace('{amount}', price.toString()));
      return;
    }

    this.gameState.coins -= price;
    this.gameState.unlockedSlots += 1;

    this.saveGameState();
    this.render();
    this.showNotification(this.t('ui.noti.slotUnlocked').replace('{count}', this.gameState.unlockedSlots.toString()));
  }

  destroy(): void {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
    }
  }
}

let gameInstance: GameManager | null = null;

function init() {
  console.log('Init function called');
  console.log('Document ready state:', document.readyState);

  const app = document.getElementById('app');
  console.log('App element:', app);

  if (!app) {
    console.error('App element not found!');
    return;
  }

  if (gameInstance) {
    console.log('Destroying old instance');
    gameInstance.destroy();
  }

  try {
    gameInstance = new GameManager(app);
    gameInstance.render();
    console.log('Virtual Pet Game initialized successfully');
  } catch (error) {
    console.error('Error initializing game:', error);
  }
}

if (document.readyState === 'loading') {
  console.log('Document still loading, adding event listener');
  document.addEventListener('DOMContentLoaded', init);
} else {
  console.log('Document already loaded, initializing immediately');
  init();
}

window.addEventListener('beforeunload', () => {
  if (gameInstance) {
    gameInstance.destroy();
  }
});

if (import.meta.hot) {
  import.meta.hot.accept(() => {
    console.log('Hot module reload');
    init();
  });
}
